// @ts-nocheck
/**
 * Algolia Custom Filters
 *
 * Reads window.algoliaCustomFilters (emitted by sections/algolia-custom-filters.liquid)
 * and builds dynamic filter UI (range sliders, toggles, refinement lists) that hook
 * into Algolia InstantSearch via beforeInstantSearchFiltersString (sliders, toggles,
 * conjunctive/multi-attr lists) and via IS disjunctive facets / helper state
 * (single-attr disjunctive refinement_lists: avoids AND conflict with native IS panel).
 *
 * All generated HTML uses acf-* class names to avoid conflicts with Algolia defaults.
 */

(function () {
  "use strict";

  const CONFIG = window.algoliaCustomFilters;
  if (!CONFIG || !CONFIG.filters || !CONFIG.filters.length) return;

  // =========================================================================
  // Analytics helpers
  // =========================================================================

  function getPageType() {
    // Detect search vs collection page
    return /\/search(\/|$|\?)/.test(window.location.pathname) ? 'search' : 'collection';
  }

  function getCollectionHandle() {
    // Extract collection handle from URL path (e.g., /collections/electronics)
    const match = window.location.pathname.match(/\/collections\/([^\/?]+)/);
    return match ? match[1] : undefined;
  }

  function getFilterEventPageType() {
    const pageType = getPageType();
    if (pageType === 'search') return pageType;
    const collectionTitle = CONFIG.collection_title;
    return typeof collectionTitle === 'string' && collectionTitle.trim()
      ? collectionTitle.trim()
      : pageType;
  }

  function publishPlpAnalyticsEvent(name, payload) {
    // Only call if OCSAnalyticsEvents helper is available
    if (typeof window.OCSAnalyticsEvents?.publishOcsAnalyticsEvent !== 'function') {
      return false;
    }
    try {
      return window.OCSAnalyticsEvents.publishOcsAnalyticsEvent(name, payload);
    } catch (error) {
      return false;
    }
  }

  // FILTER-UI (issue 4.3): the single builder for every filter_interaction event.
  // Carries the exact interaction_type + filter_category, plus an optional numeric
  // input_value for min/max updates. It never includes filter selection data or
  // free-form search text.
  function buildFilterInteractionPayload(interactionType, filterCategory, inputValue) {
    const pageType = getPageType();
    const payload = {
      location: pageType === 'search' ? 'search' : 'plp',
      page_type: pageType,
      interaction_type: interactionType,
      filter_category: filterCategory,
    };

    if (inputValue !== undefined) {
      payload.input_value = inputValue;
    }

    if (document.documentElement.lang) {
      payload.locale = document.documentElement.lang;
    }

    if (pageType === 'collection') {
      const collectionHandle = getCollectionHandle();
      if (collectionHandle) {
        payload.collection_handle = collectionHandle;
      }
    }

    return payload;
  }

  // filter_category must be language-agnostic so GA4 reporting does not fragment
  // by locale (issue 4.3 uses fixed English category names). label_en is the
  // always-English title emitted from Liquid; fall back to label only if a config
  // predates the label_en field.
  function filterCategoryLabel(config) {
    return config.label_en || config.label || '';
  }

  function shouldPublishRangeFilterInteraction(config) {
    const category = filterCategoryLabel(config);
    return category !== 'THC' && category !== 'CBD';
  }

  function publishRangeFilterInteraction(config, interactionType, inputValue) {
    if (!shouldPublishRangeFilterInteraction(config)) return false;
    return publishPlpAnalyticsEvent(
      'ocs:filter_interaction',
      buildFilterInteractionPayload(interactionType, filterCategoryLabel(config), String(inputValue)),
    );
  }

  // FILTER / FILTER-REMOVE (issue 4.1/4.2): both events carry a full 16-field
  // snapshot - filters_applied|filters_removed plus 15 fixed state fields. Every
  // unselected filter is the exact literal "No Selection" (approved casing,
  // overriding the sample's "No selection").
  const FILTER_NO_SELECTION = 'No Selection';
  const FILTER_STATE_FIELDS = [
    'product_subcategory', 'thc', 'cbd', 'availability', 'filter_size',
    'plant_type', 'filter_brand', 'filter_price', 'carrier_oil', 'allergens',
    'terpenes', 'battery_type', 'rechargeable_battery', 'savings', 'features',
  ];
  // Map each filter to its fixed GA4 event-table field by its always-English
  // title (label_en). Keys MUST equal block.settings.title verbatim (the value
  // Liquid emits as label_en); the lookup below is case-sensitive, so a casing
  // drift silently drops the filter from analytics. tests/analytics/
  // filter-field-coverage.test.mjs pins every configured facet title in the
  // committed templates against these keys so drift fails the build.
  const FILTER_FIELD_BY_TITLE = {
    'Product category': 'product_subcategory',
    'THC': 'thc',
    'CBD': 'cbd',
    'Availability': 'availability',
    'Size': 'filter_size',
    'Plant type': 'plant_type',
    'Brand': 'filter_brand',
    'Price': 'filter_price',
    'Carrier oil': 'carrier_oil',
    'Allergens': 'allergens',
    'Terpenes': 'terpenes',
    'Battery type': 'battery_type',
    'Rechargeable battery': 'rechargeable_battery',
    'Savings': 'savings',
    'Features': 'features',
  };

  function filterEventField(config) {
    if (!config) return null;
    return FILTER_FIELD_BY_TITLE[config.label_en || config.label] || null;
  }

  // InstantSearch route state stores decoded values; its qs serializer performs
  // the one required URL-encoding pass. Pre-encoding here turns "Dried Flower"
  // into "Dried%2520Flower" when the router serializes the percent sign again.
  function serializeCustomRouteValues(values) {
    return Array.from(values).join(',');
  }

  // filter_size carries the subcategory (issue 4.1 example "Dried Flower - 1g").
  // Use the facet value as-is when it already carries the subcategory (" - ");
  // otherwise prefix the single selected product_subcategory when present. S22
  // UAT confirms which shape the Algolia size facet actually returns.
  function formatFilterSizeValue(sizeValue, subcategoryValue) {
    if (sizeValue.indexOf(' - ') !== -1) return sizeValue;
    if (
      subcategoryValue &&
      subcategoryValue !== FILTER_NO_SELECTION &&
      subcategoryValue.indexOf(', ') === -1
    ) {
      return subcategoryValue + ' - ' + sizeValue;
    }
    return sizeValue;
  }

  // A boolean_list option stores the Algolia attribute it refines on as its
  // value, not a facet value: the facet value is the degenerate "true", so the
  // option's identity IS the attribute (see applyRefinementListFacets, which
  // sets value: attr). That attribute path is an index implementation detail —
  // it reported "meta.product.craft" where the merchant asked for the feature,
  // "craft" — so the event carries the last dotted segment.
  //
  // Not the configured display label ("Craft Cannabis"), unlike the toggle case
  // below. bool_labels has no _fr companion in the section schema, so the label
  // is whatever one merchant typed into one textarea and is free to change
  // without anyone thinking about GA4; the attribute leaf is the index's own
  // stable name for the feature and is what the analytics request named.
  function featureNameForAttribute(value) {
    if (typeof value !== 'string') return value;
    const leaf = value.slice(value.lastIndexOf('.') + 1);
    // A trailing dot, or no dot at all, leaves nothing to strip.
    return leaf || value;
  }

  // Full current-state snapshot: every configured filter's selected value(s)
  // joined with ", ", every unconfigured/unselected field "No Selection".
  function buildFilterStateSnapshot() {
    const snapshot = {};
    for (let i = 0; i < FILTER_STATE_FIELDS.length; i += 1) {
      snapshot[FILTER_STATE_FIELDS[i]] = FILTER_NO_SELECTION;
    }
    const filters = (CONFIG && CONFIG.filters) || [];
    // Accumulate per field so a filter that emits multiple configs for one field
    // aggregates instead of overwriting. The two Availability toggles both map to
    // the "availability" field, so an active in-store + hide-oos pair joins.
    const valuesByField = {};
    for (let i = 0; i < filters.length; i += 1) {
      const cfg = filters[i];
      const field = filterEventField(cfg);
      if (!field) continue;
      const selected = selectedValuesForFilter(cfg.id);
      if (!selected || !selected.length) continue;
      // A toggle is a boolean control; its readable event value is its on-page
      // label, not the raw "true" sentinel (analytics-team decision on U0-4:
      // emit exactly what is on the page). Use the always-English label so GA4
      // stays language-agnostic, falling back to the localized label then the
      // raw value only if no label is configured.
      let contribution = selected;
      if (cfg.type === 'toggle') {
        const toggleLabel = cfg.filter_label_en || cfg.filter_label;
        if (toggleLabel) contribution = [toggleLabel];
      } else if (cfg.type === 'boolean_list') {
        contribution = selected.map(featureNameForAttribute);
      }
      if (!valuesByField[field]) valuesByField[field] = [];
      for (let j = 0; j < contribution.length; j += 1) valuesByField[field].push(contribution[j]);
    }
    for (let i = 0; i < FILTER_STATE_FIELDS.length; i += 1) {
      const field = FILTER_STATE_FIELDS[i];
      if (valuesByField[field] && valuesByField[field].length) {
        snapshot[field] = valuesByField[field].join(', ');
      }
    }
    if (snapshot.filter_size !== FILTER_NO_SELECTION) {
      snapshot.filter_size = snapshot.filter_size
        .split(', ')
        .map(function (value) { return formatFilterSizeValue(value, snapshot.product_subcategory); })
        .join(', ');
    }
    return snapshot;
  }

  // Build the event-table payload for filter_event / filter_removed. actedKey is
  // "filters_applied" or "filters_removed"; actedField is the acted filter's field
  // name (or a literal such as "All Filters" for clear-all).
  function buildFilterEventTablePayload(actedKey, actedField) {
    const pageType = getPageType();
    const payload = {
      location: pageType === 'search' ? 'search' : 'plp',
      page_type: getFilterEventPageType(),
    };
    payload[actedKey] = actedField || FILTER_NO_SELECTION;
    const snapshot = buildFilterStateSnapshot();
    for (let i = 0; i < FILTER_STATE_FIELDS.length; i += 1) {
      payload[FILTER_STATE_FIELDS[i]] = snapshot[FILTER_STATE_FIELDS[i]];
    }
    if (document.documentElement.lang) payload.locale = document.documentElement.lang;
    if (pageType === 'collection') {
      const collectionHandle = getCollectionHandle();
      if (collectionHandle) payload.collection_handle = collectionHandle;
    }
    return payload;
  }

  function publishFilterEvent(actedField) {
    // Fire only for a mapped inventory filter; an unmapped title is out of the
    // 16-field event-table scope (S22 verifies every configured filter maps).
    if (!actedField) return;
    publishPlpAnalyticsEvent('ocs:filter_event', buildFilterEventTablePayload('filters_applied', actedField));
  }

  function publishFilterRemove(actedField) {
    if (!actedField) return;
    publishPlpAnalyticsEvent('ocs:filter_remove', buildFilterEventTablePayload('filters_removed', actedField));
  }

  // [/FILTER_ANALYTICS_SCOPE] End of the FILTER / FILTER-REMOVE snapshot + publisher
  // helpers. Test harnesses slice from `const FILTER_NO_SELECTION` to this marker to
  // run the real filterEventField/buildFilterStateSnapshot in a VM. Keep this sentinel
  // directly above publishViewItemsInteraction so the slice stays stable if the
  // function is renamed.
  function publishViewItemsInteraction() {
    // View Items is mobile-only and unrelated to a filter group, so it passes the
    // exact filter_category "N/A" (FILTER-UI-003).
    publishPlpAnalyticsEvent('ocs:filter_interaction', buildFilterInteractionPayload('view_items', 'N/A'));
  }

  function selectedValuesForFilter(filterId) {
    const st = _state[filterId];
    if (!st) return [];
    if (st.type === 'range_slider') {
      // A slider left at its full default bounds is not a selection, so it maps
      // to "No Selection" in the event-table snapshot (FILTER-004).
      if (st.currentMin <= st.dataMin && st.currentMax >= st.dataMax) return [];
      return [st.currentMin + '-' + st.currentMax];
    }
    if (st.type === 'toggle') {
      return st.active ? ['true'] : [];
    }
    if (st.type === 'refinement_list' || st.type === 'boolean_list' || st.type === 'tag_list') {
      return Array.from(st.selected || []);
    }
    return [];
  }

  // =========================================================================
  // State
  // =========================================================================

  const _state = {};
  let _inPopstate = false;
  let _triggerSearch = null;
  let _searchClient = null;
  let _algoliaIndex = null;
  let _indexName = null;
  let _resultsReceived = false; // true after first IS result: used to distinguish loading vs no-data
  let _initialized = false; // guard against double-init (theme editor re-renders, duplicate events)
  const _visibilityAttrData = {}; // attr -> true (has data) | false (no data): populated by fetchInitialBounds
  let _inStoreHasItems = null; // null = not yet fetched, true/false after fetchInitialBounds
  let _oosHasItems = null; // null = not yet fetched, true/false after fetchInitialBounds
  const _appliedFilterOrder = [];
  let _excludeModeCountRequestSeq = 0;
  let _mobileFilterAnchor = null;
  // Query-level params override Algolia dashboard afterDistinct settings; keep PLP facet counts before distinct globally.
  const FACETING_AFTER_DISTINCT = false;
  // Algolia multipleQueries accepts up to 50 queries per request.
  const EXCLUDE_MODE_COUNT_QUERY_BATCH_SIZE = 50;

  // =========================================================================
  // Mobile viewport stability
  // =========================================================================

  function isMobileFilterDrawerOpen() {
    if (!window.matchMedia || !window.matchMedia("(max-width: 799px)").matches)
      return false;
    const sidebar = document.querySelector(".acf-sidebar");
    return Boolean(sidebar && sidebar.classList.contains("acf-sidebar--open"));
  }

  function rememberMobileFilterAnchor(control, config) {
    if (!isMobileFilterDrawerOpen() || !control || !config) return;

    const wrapper = control.closest(".acf-filter");
    if (!wrapper) return;
    let kind = "header";
    let value = "";
    let index = 0;

    if (control.classList.contains("acf-refine-checkbox")) {
      kind = "refinement";
      value = control.value;
      index = Array.from(
        wrapper.querySelectorAll(".acf-refine-checkbox"),
      ).indexOf(control);
    } else if (control.classList.contains("acf-toggle-checkbox")) {
      kind = "toggle";
    } else if (control.classList.contains("acf-handle--lower")) {
      kind = "range-lower";
    } else if (control.classList.contains("acf-handle--upper")) {
      kind = "range-upper";
    }

    _mobileFilterAnchor = {
      filterId: config.id,
      kind: kind,
      value: value,
      index: Math.max(index, 0),
    };
  }

  function resolveMobileFilterAnchor() {
    if (!_mobileFilterAnchor) return null;

    const anchor = _mobileFilterAnchor;
    const escapedId = CSS.escape(String(anchor.filterId));
    let wrapper = document.querySelector(
      '.acf-filter[data-filter-id="' + escapedId + '"]',
    );
    let control = null;

    if (anchor.kind === "toggle") {
      const item = document.querySelector(
        '.acf-item[data-filter-id="' + escapedId + '"]',
      );
      if (item) {
        wrapper = item.closest(".acf-filter");
        control = item.querySelector(".acf-toggle-checkbox");
      }
    } else if (wrapper && anchor.kind === "refinement") {
      control = wrapper.querySelector(
        '.acf-refine-checkbox[value="' + CSS.escape(anchor.value) + '"]',
      );
      if (!control) {
        const controls = wrapper.querySelectorAll(".acf-refine-checkbox");
        control = controls[Math.min(anchor.index, controls.length - 1)] || null;
      }
    } else if (wrapper && anchor.kind === "range-lower") {
      control = wrapper.querySelector(".acf-handle--lower");
    } else if (wrapper && anchor.kind === "range-upper") {
      control = wrapper.querySelector(".acf-handle--upper");
    }

    return control || wrapper?.querySelector(":scope > .acf-header") || null;
  }

  function captureMobileFilterViewport() {
    if (!isMobileFilterDrawerOpen()) return null;
    const scrollContainer = document.querySelector(
      ".acf-sidebar .acf-sidebar-content",
    );
    const control = resolveMobileFilterAnchor();
    if (!scrollContainer || !control) return null;

    return {
      scrollContainer: scrollContainer,
      top: control.getBoundingClientRect().top,
      restoreFocus: document.activeElement === control,
    };
  }

  function restoreMobileFilterViewport(snapshot) {
    if (!snapshot || !isMobileFilterDrawerOpen()) return;
    const control = resolveMobileFilterAnchor();
    if (!control) return;

    const topDelta = control.getBoundingClientRect().top - snapshot.top;
    if (Math.abs(topDelta) > 0.5) {
      snapshot.scrollContainer.scrollTop += topDelta;
    }
    if (snapshot.restoreFocus) {
      control.focus({ preventScroll: true });
    }
  }

  // =========================================================================
  // Applied filter chip ordering
  // =========================================================================

  function getFilterOrderKey(id, value) {
    return id + "::" + (value == null ? "" : String(value));
  }

  function rememberAppliedFilter(id, value) {
    const key = getFilterOrderKey(id, value);
    if (!_appliedFilterOrder.includes(key)) {
      _appliedFilterOrder.push(key);
    }
  }

  function forgetAppliedFilter(id, value) {
    const key = getFilterOrderKey(id, value);
    const index = _appliedFilterOrder.indexOf(key);
    if (index !== -1) {
      _appliedFilterOrder.splice(index, 1);
    }
  }

  function sortActiveFiltersByAppliedOrder(active) {
    const activeKeys = active.map((filter) =>
      getFilterOrderKey(filter.id, filter.value),
    );
    const activeKeySet = new Set(activeKeys);

    for (let i = _appliedFilterOrder.length - 1; i >= 0; i--) {
      if (!activeKeySet.has(_appliedFilterOrder[i])) {
        _appliedFilterOrder.splice(i, 1);
      }
    }

    activeKeys.forEach((key) => {
      if (!_appliedFilterOrder.includes(key)) {
        _appliedFilterOrder.push(key);
      }
    });

    const orderIndex = new Map(
      _appliedFilterOrder.map((key, index) => [key, index]),
    );

    return active.slice().sort((a, b) => {
      const aIndex = orderIndex.get(getFilterOrderKey(a.id, a.value));
      const bIndex = orderIndex.get(getFilterOrderKey(b.id, b.value));
      return (aIndex ?? 0) - (bIndex ?? 0);
    });
  }

  // =========================================================================
  // Format helpers
  // =========================================================================

  function fmtValue(val, unit) {
    return unit ? `${val}${unit}` : `${val}`;
  }

  /**
   * Returns true for facet values that must be quoted in Algolia filter strings.
   * Algolia interprets a leading `-` as its NOT operator, and `(`, `)`, `:` have
   * reserved meaning. Wrapping such values in double-quotes makes them literals.
   * Used to decide whether a value should go through IS's native disjunctive facet
   * path (which emits unquoted) or through the filters string (which quotes correctly).
   */
  function _needsQuoting(val) {
    if (typeof val !== "string" || !val) return false;
    return val.charAt(0) === "-" || val.indexOf("(") !== -1 || val.indexOf(")") !== -1 || val.indexOf(":") !== -1;
  }

  function _quoteAlgoliaFilterValue(val) {
    return JSON.stringify(String(val));
  }

  function escapeHTML(str) {
    return (str || "")
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;");
  }

  function escapeAttr(str) {
    return (str || "")
      .replace(/&/g, "&amp;")
      .replace(/"/g, "&quot;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;");
  }

  function formatCount(n) {
    return n.toLocaleString();
  }

  // =========================================================================
  // Algolia client (for disjunctive extra counts batch)
  // =========================================================================

  function getAttrs(config) {
    // Bool-labels mode: attributes are the keys of the bool map
    if (config._parsedBoolLabels) return Object.keys(config._parsedBoolLabels);
    if (config.bool_labels) {
      const m = parseBoolLabels(config.bool_labels);
      if (m) {
        config._parsedBoolLabels = m;
        return Object.keys(m);
      }
    }
    if (!config.attribute) return [];
    return config.attribute
      .split(",")
      .map(function (a) {
        return a.trim();
      })
      .filter(Boolean);
  }

  function parseBoolLabels(raw) {
    if (!raw) return null;
    const map = {};
    raw.split("\n").forEach(function (line) {
      line = line.trim();
      if (!line) return;
      const idx = line.indexOf(":");
      if (idx === -1) return;
      const attr = line.slice(0, idx).trim();
      const label = line.slice(idx + 1).trim();
      if (attr) map[attr] = label || attr;
    });
    return Object.keys(map).length ? map : null;
  }

  function appendTemporaryEnglishProductFilter(filterString) {
    const languageFilter = window.algoliaLanguageFilters?.filterForSource?.('products') || '';
    if (!languageFilter) return filterString || '';
    if (!filterString) return languageFilter;
    if (filterString.includes(languageFilter)) return filterString;
    return '(' + filterString + ') AND (' + languageFilter + ')';
  }

  function getCollectionFilter() {
    // Prefer Shopify's numeric collection ID: matches Algolia's native collection_ids filter format
    const resourceId = window.ShopifyAnalytics?.meta?.page?.resourceId;
    if (resourceId) {
      const f = appendTemporaryEnglishProductFilter('collection_ids:"' + resourceId + '"');
      console.log('[ACF DEBUG] getCollectionFilter result:', f);
      return f;
    }
    // Fallback to collection_handles from URL
    const parts = window.location.pathname.split("/");
    const idx = parts.indexOf("collections");
    if (idx !== -1 && parts[idx + 1] && parts[idx + 1] !== "all") {
      const f = appendTemporaryEnglishProductFilter("collection_handles:" + parts[idx + 1]);
      console.log('[ACF DEBUG] getCollectionFilter result:', f);
      return f;
    }
    const languageFilter = appendTemporaryEnglishProductFilter("");
    console.log('[ACF DEBUG] getCollectionFilter result:', languageFilter || '(empty: no collection match)');
    return languageFilter;
  }

  // Returns the effective index prefix, normalised to always end with "_".
  // Theme setting takes priority over the app config value; if the theme setting
  // is blank the app value is used as-is (already has trailing "_").
  function resolveIndexPrefix() {
    const themeP = window.themeSettings?.algoliaIndexPrefix;
    const appP = window.algoliaShopify?.config?.index_prefix || "";
    const raw = (themeP && themeP.trim()) ? themeP.trim() : appP;
    return raw && !raw.endsWith("_") ? raw + "_" : raw;
  }

  // Map IS's internal helper index to the index the grid actually queries, so
  // custom-filter count requests match the (rewritten) grid. Mirrors the
  // app->theme rewrite in algolia-plp-hooks.js beforeInstantSearchOptions.
  function resolveQueriedIndex() {
    const isSearch = /\/search(\/|$|\?)/.test(window.location.pathname);
    if (isSearch) return _indexName;                 // search replica (unchanged)
    const helperIdx = window._algoliaHelper?.getIndex();
    if (!helperIdx) return _indexName;
    const isFR = window.Shopify?.locale?.startsWith("fr");
    const themePrefix = resolveIndexPrefix();
    const appRaw = window.algoliaShopify?.config?.index_prefix || "";
    const appPrefix = appRaw && !appRaw.endsWith("_") ? appRaw + "_" : appRaw;
    const appBase = appPrefix + (isFR ? "products_canada_fr" : "products");
    if (helperIdx === appBase) return _indexName;     // base -> theme Primary (matches grid)
    if (appPrefix && appPrefix !== themePrefix && helperIdx.startsWith(appPrefix)) {
      return themePrefix + helperIdx.slice(appPrefix.length); // sort replica prefix remap
    }
    return helperIdx;                                 // already theme (e.g. sort replica)
  }

  function initAlgoliaClient() {
    if (_algoliaIndex) return true;
    try {
      const algoliasearch = window.algoliaShopify?.externals?.algoliasearch;
      const config = window.algoliaShopify?.config;
      if (!algoliasearch || !config) return false;
      const appId = config.app_id || config.application_id || config.appId;
      const apiKey =
        config.search_api_key || config.searchApiKey || config.apiKey;
      const prefix = resolveIndexPrefix();
      if (!appId || !apiKey) return false;
      _searchClient = algoliasearch(appId, apiKey);
      const isFR = window.Shopify?.locale?.startsWith("fr");
      const isSearchPage = /\/search(\/|$|\?)/.test(window.location.pathname);
      if (isSearchPage) {
        const configuredSuffix = isFR
          ? window.themeSettings?.searchIndexes?.fr
          : window.themeSettings?.searchIndexes?.en;
        const suffix = configuredSuffix || (isFR ? "search_products_canada_fr" : "search_products");
        _indexName = prefix + suffix;
      } else {
        const primarySuffix = isFR
          ? window.themeSettings?.primaryIndexes?.fr || "products_canada_fr"
          : window.themeSettings?.primaryIndexes?.en || "products";
        _indexName = prefix + primarySuffix;
      }
      _algoliaIndex = _searchClient.initIndex(_indexName);
      return true;
    } catch (e) {
      console.warn("[acf] initAlgoliaClient failed:", e);
      return false;
    }
  }

  /**
   * Returns true for single-attribute disjunctive refinement_list filters.
   * These are injected into IS via getWidgetSearchParameters (native IS disjunctive facets),
   * NOT via the filters string. This avoids ANDing with the native Algolia IS panel's
   * facetFilters for the same attribute, which would produce 0 results.
   *
   * Returns false when param_mode === 'multi': those filters use url_param routing
   * instead of the native IS disjunctive path, even for a single-attribute disjunctive filter.
   */
  function isNativeDisjunctive(cfg) {
    if (cfg.exclude_mode) {
      return false;
    }
    if (cfg.param_mode === "multi") return false;
    return (
      (cfg.type === "refinement_list" || cfg.type === "tag_list") &&
      cfg.operator_type === "disjunctive" &&
      cfg.attribute &&
      !cfg.attribute.includes(",")
    );
  }

  function buildFilterStringExcluding(excludeId, options) {
    const parts = [];
    const includeInStore = options && options.includeInStore === true;
    // window._algoliaBaseFilter is set by helper-capture widget at IS init: contains
    // the exact collection_ids:"..." filter Algolia's app embed uses, captured before
    // any custom filters are applied.
    const capturedBaseFilter = window._algoliaBaseFilter || getCollectionFilter();
    const collFilter = appendTemporaryEnglishProductFilter(capturedBaseFilter);
    if (collFilter) parts.push(collFilter);
    const inStoreCfg = CONFIG.filters.find(function(f) { return f.url_param === 'show_in_store'; });
    if (inStoreCfg && !_isInStoreToggleActive() && !includeInStore) parts.push(IN_STORE_FILTER);
    for (const id of Object.keys(_state)) {
      if (id === excludeId) continue;
      const st = _state[id];
      if (st.type === "range_slider") {
        const cfg = st.config;
        if (st.currentMin > st.dataMin || st.currentMax < st.dataMax) {
          const fp = [];
          const maxAttr = getEffectiveMaxAttr(cfg);
          if (cfg._facetOverride) {
            fp.push(maxAttr + ">=" + st.currentMin);
          } else if (isMinAttributeEnabled(cfg)) {
            fp.push(cfg.min_attribute + ">=" + st.currentMin);
          }
          fp.push(maxAttr + "<=" + st.currentMax);
          parts.push(fp.join(" AND "));
        }
      }
      if (st.type === "toggle") {
        const cfg = st.config;
        if (cfg.mode === "exclude") {
          if (!st.active && cfg.exclude_filter) {
            parts.push(cfg.exclude_filter);
          }
        } else {
          if (st.active) {
            const f = cfg.include_filter || (cfg.attribute ? cfg.attribute + cfg.operator + cfg.value : null);
            if (f) parts.push(f);
          }
        }
      }
      if (
        (st.type === "refinement_list" ||
          st.type === "boolean_list" ||
          st.type === "tag_list") &&
        st.selected.size > 0
      ) {
        const cfg = st.config;
        // Exclude mode: emit NOT attr:"value" AND NOT attr:"value" for each checked item.
        if (cfg.exclude_mode && st.selected.size > 0) {
          var attrs = getAttrs(cfg);
          var notClauses = [...st.selected].map(function (v) {
            var knownAttr = st.valueAttrMap && st.valueAttrMap.get(v);
            if (knownAttr) return 'NOT ' + knownAttr + ':"' + v + '"';
            if (attrs.length === 1) return 'NOT ' + attrs[0] + ':"' + v + '"';
            return 'NOT (' + attrs.map(function (a) { return a + ':"' + v + '"'; }).join(' OR ') + ')';
          });
          parts.push(notClauses.join(' AND '));
          continue;
        }
        let vals;
        if (cfg._parsedBoolLabels) {
          vals = [...st.selected].map(function (v) {
            return v + ':"true"';
          });
        } else {
          const attrs = getAttrs(cfg);
          vals = [...st.selected].map(function (v) {
            const knownAttr = st.valueAttrMap && st.valueAttrMap.get(v);
            if (knownAttr) return knownAttr + ':"' + v + '"';
            return (
              "(" +
              attrs
                .map(function (a) {
                  return a + ':"' + v + '"';
                })
                .join(" OR ") +
              ")"
            );
          });
        }
        if (cfg.operator_type === "disjunctive") {
          parts.push("(" + vals.join(" OR ") + ")");
        } else {
          parts.push(vals.join(" AND "));
        }
      }
    }
    return parts.join(" AND ");
  }

  function refreshExtraCounts(selectedListIds) {
    if (!selectedListIds.length) return;
    if (!initAlgoliaClient()) return;
    const currentIndex = resolveQueriedIndex();
    const queries = selectedListIds.map(function (id) {
      const cfg = CONFIG.filters.find(function (c) {
        return c.id === id;
      });
      const filterStr = buildFilterStringExcluding(id);
      return {
        indexName: currentIndex,
        params: {
          facets: getAttrs(cfg),
          filters: filterStr,
          query: window._algoliaHelper?.state?.query || "",
          hitsPerPage: 0,
          analytics: false,
          maxValuesPerFacet: 1000,
          distinct: true,
          facetingAfterDistinct: FACETING_AFTER_DISTINCT,
          ruleContexts: window._algoliaRuleContexts || [],
        },
      };
    });
    _searchClient
      .multipleQueries(queries)
      .then(function (response) {
        response.results.forEach(function (result, i) {
          const id = selectedListIds[i];
          const cfg = CONFIG.filters.find(function (c) {
            return c.id === id;
          });
          applyRefinementListFacets(cfg, result.facets || {});
        });
        refreshFilterDOM();
        refreshExcludeModeOptionCounts();
      })
      .catch(function (e) {
        console.warn("[acf] refreshExtraCounts failed:", e);
        refreshExcludeModeOptionCounts();
      });
  }

  function buildExcludeModeNotClause(config, st, value) {
    const attrs = getAttrs(config);
    if (!attrs.length) return "";
    const knownAttr = st.valueAttrMap && st.valueAttrMap.get(value);
    if (knownAttr) return 'NOT ' + knownAttr + ':"' + value + '"';
    if (attrs.length === 1) return 'NOT ' + attrs[0] + ':"' + value + '"';
    return 'NOT (' + attrs.map(function (attr) {
      return attr + ':"' + value + '"';
    }).join(' OR ') + ')';
  }

  function refreshExcludeModeOptionCounts() {
    const excludeConfigs = CONFIG.filters.filter(function (cfg) {
      if (!cfg.exclude_mode) return false;
      return cfg.type === "refinement_list" || cfg.type === "tag_list";
    });
    if (!excludeConfigs.length) return;
    if (!initAlgoliaClient()) return;

    const queryMeta = [];
    const queries = [];
    const requestId = ++_excludeModeCountRequestSeq;
    const currentIndex = resolveQueriedIndex();
    const baseFilter = buildFilterStringExcluding("__acf_no_excluded_filter__");
    const query = window._algoliaHelper?.state?.query || "";
    const ruleContexts = window._algoliaRuleContexts || [];

    excludeConfigs.forEach(function (cfg) {
      const st = _state[cfg.id];
      if (!st || !st.facetValues || !st.facetValues.length) return;

      st.facetValues.forEach(function (facetValue) {
        const notClause = buildExcludeModeNotClause(cfg, st, facetValue.value);
        if (!notClause) return;
        const filters = baseFilter ? baseFilter + " AND " + notClause : notClause;
        queryMeta.push({ id: cfg.id, value: facetValue.value });
        queries.push({
          indexName: currentIndex,
          params: {
            filters: filters,
            query: query,
            hitsPerPage: 0,
            analytics: false,
            distinct: true,
            facetingAfterDistinct: FACETING_AFTER_DISTINCT,
            ruleContexts: ruleContexts,
          },
        });
      });
    });

    if (!queries.length) return;

    const chunks = [];
    for (let i = 0; i < queries.length; i += EXCLUDE_MODE_COUNT_QUERY_BATCH_SIZE) {
      chunks.push({
        queries: queries.slice(i, i + EXCLUDE_MODE_COUNT_QUERY_BATCH_SIZE),
        meta: queryMeta.slice(i, i + EXCLUDE_MODE_COUNT_QUERY_BATCH_SIZE),
      });
    }

    Promise.all(chunks.map(function (chunk) {
      return _searchClient.multipleQueries(chunk.queries).then(function (response) {
        return { response: response, meta: chunk.meta };
      });
    }))
      .then(function (chunkResponses) {
        if (requestId !== _excludeModeCountRequestSeq) return;
        chunkResponses.forEach(function (chunkResponse) {
          const results = chunkResponse.response.results || [];
          results.forEach(function (result, i) {
            const meta = chunkResponse.meta[i];
            const st = meta && _state[meta.id];
            if (!st || !st.facetValues) return;
            const facetValue = st.facetValues.find(function (item) {
              return item.value === meta.value;
            });
            if (facetValue) facetValue.count = result.nbHits || 0;
          });
        });
        refreshFilterDOM();
      })
      .catch(function (e) {
        console.warn("[acf] refreshExcludeModeOptionCounts failed:", e);
      });
  }

  function refreshDynamicSliderBounds(sliderIds) {
    if (!sliderIds.length) return;
    if (!initAlgoliaClient()) return;
    const currentIndex = resolveQueriedIndex();
    const queries = sliderIds.map(function (id) {
      const cfg = CONFIG.filters.find(function (c) { return c.id === id; });
      const filterStr = buildFilterStringExcluding(id);
      const helper = window._algoliaHelper;
      const facetFilters = [];
      const disjRefs = (helper && helper.state && helper.state.disjunctiveFacetsRefinements) || {};
      Object.keys(disjRefs).forEach(function (attr) {
        const vals = disjRefs[attr];
        if (vals && vals.length) facetFilters.push(vals.map(function (v) { return attr + ':' + v; }));
      });
      const conjRefs = (helper && helper.state && helper.state.facetsRefinements) || {};
      Object.keys(conjRefs).forEach(function (attr) {
        const vals = conjRefs[attr];
        if (vals && vals.length) vals.forEach(function (v) { facetFilters.push([attr + ':' + v]); });
      });
      const facetAttrs = [];
      if (cfg.min_attribute) facetAttrs.push(cfg.min_attribute); // kept for future min-bound support
      const maxAttr = getEffectiveMaxAttr(cfg);
      if (maxAttr && maxAttr !== cfg.min_attribute) facetAttrs.push(maxAttr);
      const params = {
        facets: facetAttrs,
        filters: filterStr,
        query: (helper && helper.state && helper.state.query) || '',
        hitsPerPage: 0,
        analytics: false,
        facetingAfterDistinct: FACETING_AFTER_DISTINCT,
        ruleContexts: window._algoliaRuleContexts || [],
      };
      if (facetFilters.length) params.facetFilters = facetFilters;
      return { indexName: currentIndex, params };
    });
    _searchClient
      .multipleQueries(queries)
      .then(function (response) {
        response.results.forEach(function (result, i) {
          const id = sliderIds[i];
          const cfg = CONFIG.filters.find(function (c) { return c.id === id; });
          const st = _state[id];
          if (!st || !cfg) return;
          const facetsStats = result.facets_stats || {};
          const maxAttr = getEffectiveMaxAttr(cfg);
          const maxStats = facetsStats[maxAttr];
          if (!maxStats) return;
          const newDataMax = Math.ceil(maxStats.max ?? st.dataMax);
          if (newDataMax === st.dataMax) return;
          st.dataMax = newDataMax;
          if (!st.userInteracted || st.currentMax >= st.dataMax) {
            // Handle is at the ceiling (not an intentional restriction): follow new max.
            st.currentMax = newDataMax;
          } else if (st.currentMax > newDataMax) {
            // User dragged below the old max; clamp down if the new max is lower.
            st.currentMax = newDataMax;
          }
        });
        refreshFilterDOM();
      })
      .catch(function (e) {
        console.warn('[acf] refreshDynamicSliderBounds failed:', e);
      });
  }

  function getToggleCountFilter(cfg) {
    if (!cfg || cfg.type !== "toggle") return "";
    if (cfg.url_param === "show_in_store") {
      const attr = cfg.visibility_attribute || "meta.variant.availability";
      const value = cfg.visibility_value || "in-store";
      return attr + ":" + _quoteAlgoliaFilterValue(value);
    }
    if (cfg.include_filter) return cfg.include_filter;
    if (cfg.attribute && cfg.mode !== "exclude") {
      return cfg.attribute + cfg.operator + cfg.value;
    }
    return "";
  }

  function refreshToggleCounts() {
    const toggleConfigs = CONFIG.filters.filter(function (cfg) {
      return getToggleCountFilter(cfg) !== "";
    });
    if (!toggleConfigs.length) return;
    if (!initAlgoliaClient()) return;

    const currentIndex = resolveQueriedIndex();
    const queries = toggleConfigs.map(function (cfg) {
      const isInStoreToggle = cfg.url_param === "show_in_store";
      const baseFilter = buildFilterStringExcluding(cfg.id, {
        includeInStore: isInStoreToggle,
      });
      const toggleFilter = getToggleCountFilter(cfg);
      const parts = baseFilter ? [baseFilter, toggleFilter] : [toggleFilter];
      return {
        indexName: currentIndex,
        params: {
          filters: parts.join(" AND "),
          query: window._algoliaHelper?.state?.query || "",
          hitsPerPage: 0,
          analytics: false,
          distinct: true,
          facetingAfterDistinct: FACETING_AFTER_DISTINCT,
          ruleContexts: window._algoliaRuleContexts || [],
        },
      };
    });

    _searchClient
      .multipleQueries(queries)
      .then(function (response) {
        response.results.forEach(function (result, i) {
          const cfg = toggleConfigs[i];
          const li = document.querySelector('.acf-item[data-filter-id="' + cfg.id + '"]');
          if (!li) return;
          const countEl = li.querySelector(".acf-toggle-count");
          if (!countEl) return;
          countEl.textContent = formatCount(result.nbHits || 0);
        });
      })
      .catch(function (e) {
        console.warn("[acf] refreshToggleCounts failed:", e);
      });
  }

  // =========================================================================
  // Range Slider
  // =========================================================================

  function initRangeSliderState(config) {
    _state[config.id] = {
      type: "range_slider",
      config,
      dataMin: 0,
      dataMax: 1000,
      currentMin: 0,
      currentMax: 1000,
      userInteracted: false,
    };
  }

  function applyRangeSliderFacetsStats(config, facetsStats) {
    const st = _state[config.id];
    if (!st || !facetsStats) return;
    // Bounds are fixed after initial fetch: skip all IS search result updates
    if (st._boundsReady || st._boundsLoading) return;

    const minStats = isMinAttributeEnabled(config)
      ? facetsStats[config.min_attribute]
      : undefined;
    const maxStats = facetsStats[getEffectiveMaxAttr(config)];
    if (!minStats && !maxStats) return;

    const newDataMin = Math.floor(minStats?.min ?? 0);
    const newDataMax = Math.ceil(maxStats?.max ?? minStats?.max ?? st.dataMax);

    st.dataMin = newDataMin;
    st.dataMax = newDataMax;

    if (!st.userInteracted) {
      st.currentMin = newDataMin;
      st.currentMax = newDataMax;
    } else {
      if (st.currentMin < newDataMin) st.currentMin = newDataMin;
      if (st.currentMax > newDataMax) st.currentMax = newDataMax;
    }
  }

  /**
   * Fetch slider bounds once on init using the collection filter only: no user filters.
   * Marks _boundsLoading immediately to block IS search results from setting stale bounds
   * during the async fetch, then sets _boundsReady when done.
   */
  function fetchInitialBounds() {
    const sliderConfigs = CONFIG.filters.filter(function (c) {
      return c.type === "range_slider";
    });
    const visibilityToggleConfigs = CONFIG.filters.filter(function (c) {
      return c.type === "toggle" && c.visibility_attribute;
    });
    if (!sliderConfigs.length && !visibilityToggleConfigs.length) return;

    // Block onInstantSearchResult from touching bounds while fetch is in flight.
    // Set before initAlgoliaClient so IS results can't sneak in if client init fails.
    sliderConfigs.forEach(function (cfg) {
      const st = _state[cfg.id];
      if (st) st._boundsLoading = true;
    });

    if (!initAlgoliaClient()) {
      // Client unavailable: unblock so IS results can still seed bounds
      sliderConfigs.forEach(function (cfg) {
        const st = _state[cfg.id];
        if (st) st._boundsLoading = false;
      });
      return;
    }

    const facetAttrs = [];
    sliderConfigs.forEach(function (cfg) {
      if (cfg.min_attribute) facetAttrs.push(cfg.min_attribute);
      if (cfg.max_attribute && cfg.max_attribute !== cfg.min_attribute)
        facetAttrs.push(cfg.max_attribute);
    });
    // Also request visibility_attribute facets from toggle configs (e.g. meta.variant.availability)
    CONFIG.filters.forEach(function (cfg) {
      if (cfg.type === "toggle" && cfg.visibility_attribute && !facetAttrs.includes(cfg.visibility_attribute)) {
        facetAttrs.push(cfg.visibility_attribute);
      }
    });
    // Also request in-store availability facet when in-store toggle is configured
    const inStoreCfgForFetch = CONFIG.filters.find(function (c) { return c.url_param === 'show_in_store'; });
    if (inStoreCfgForFetch) {
      const inStoreAttr = inStoreCfgForFetch.visibility_attribute || 'meta.variant.availability';
      if (!facetAttrs.includes(inStoreAttr)) facetAttrs.push(inStoreAttr);
    }
    // Also request OOS facet when hide_oos toggle is configured
    const oosCfgForFetch = CONFIG.filters.find(function (c) { return c.url_param === 'hide_oos'; });
    if (oosCfgForFetch) {
      const oosAttr = oosCfgForFetch.visibility_attribute || 'inventory_quantity';
      if (!facetAttrs.includes(oosAttr)) facetAttrs.push(oosAttr);
    }

    const collFilter = getCollectionFilter();

    _algoliaIndex
      .search("", {
        facets: facetAttrs,
        filters: collFilter || "",
        hitsPerPage: 0,
        analytics: false,
        maxValuesPerFacet: 1000,
        distinct: true,
        facetingAfterDistinct: FACETING_AFTER_DISTINCT,
        ruleContexts: window._algoliaRuleContexts || [],
      })
      .then(function (result) {
        const facetsStats = result.facets_stats || {};
        const resultFacets = result.facets || {};
        sliderConfigs.forEach(function (cfg) {
          const st = _state[cfg.id];
          if (!st) return;
          st._boundsLoading = false;
          applyRangeSliderFacetsStats(cfg, facetsStats);
          st._boundsReady = true;
        });
        // Populate visibility data for toggle groups (e.g. Availability)
        CONFIG.filters.forEach(function (cfg) {
          if (cfg.type === "toggle" && cfg.visibility_attribute) {
            const attr = cfg.visibility_attribute;
            const attrFacet = resultFacets[attr] || {};
            _visibilityAttrData[attr] = Object.keys(attrFacet).length > 0;
          }
        });
        // Determine if any in-store products exist for this collection
        if (inStoreCfgForFetch) {
          const inStoreAttr2 = inStoreCfgForFetch.visibility_attribute || 'meta.variant.availability';
          const inStoreVal2 = inStoreCfgForFetch.visibility_value || 'in-store';
          const availFacet = resultFacets[inStoreAttr2] || {};
          _inStoreHasItems = (availFacet[inStoreVal2] || 0) > 0;
        }
        // Determine if any out-of-stock products exist for this collection
        if (oosCfgForFetch) {
          const oosAttr2 = oosCfgForFetch.visibility_attribute || 'inventory_quantity';
          const invFacets = resultFacets[oosAttr2] || {};
          _oosHasItems = Object.keys(invFacets).some(function (k) { return parseFloat(k) <= 0; });
        }

        refreshFilterDOM();
      })
      .catch(function (e) {
        console.warn("[acf] fetchInitialBounds failed:", e);
        // On failure, unblock so IS results can still seed bounds
        sliderConfigs.forEach(function (cfg) {
          const st = _state[cfg.id];
          if (st) st._boundsLoading = false;
        });
      });
  }

  /**
   * Re-fetch bounds for sliders that used a title-match override (_facetOverride).
   * Called from injectFilters() after overrides are set, because fetchInitialBounds()
   * ran at init() time before _facetOverride was known.
   */
  function fetchOverrideBounds() {
    const overrideConfigs = CONFIG.filters.filter(function (c) {
      return c.type === "range_slider" && c._facetOverride;
    });
    if (!overrideConfigs.length) return;
    if (!initAlgoliaClient()) return;

    overrideConfigs.forEach(function (cfg) {
      const st = _state[cfg.id];
      if (st) st._boundsLoading = true;
    });

    const facetAttrs = overrideConfigs.map(function (cfg) {
      return cfg._facetOverride;
    });
    const collFilter = getCollectionFilter();

    _algoliaIndex
      .search("", {
        facets: facetAttrs,
        filters: collFilter || "",
        hitsPerPage: 0,
        analytics: false,
        maxValuesPerFacet: 1000,
        distinct: true,
        facetingAfterDistinct: FACETING_AFTER_DISTINCT,
        ruleContexts: window._algoliaRuleContexts || [],
      })
      .then(function (result) {
        const facetsStats = result.facets_stats || {};
        overrideConfigs.forEach(function (cfg) {
          const st = _state[cfg.id];
          if (!st) return;
          st._boundsLoading = false;
          applyRangeSliderFacetsStats(cfg, facetsStats);
          st._boundsReady = true;
        });
        refreshFilterDOM();
      })
      .catch(function (e) {
        console.warn("[acf] fetchOverrideBounds failed:", e);
        overrideConfigs.forEach(function (cfg) {
          const st = _state[cfg.id];
          if (st) st._boundsLoading = false;
        });
      });
  }

  // =========================================================================
  // Accordion accessibility helper
  // =========================================================================

  function setupAccordionA11y(wrapper) {
    if (wrapper._acfA11yReady) return; // already wired up (e.g. grouped toggles)
    wrapper._acfA11yReady = true;

    const btn = wrapper.querySelector(":scope > .acf-header");
    if (!btn) return;

    // Toggle open/close on click: button handles Enter/Space natively
    btn.addEventListener("click", () => {
      const isOpen = wrapper.classList.toggle("is-open");
      btn.setAttribute("aria-expanded", isOpen ? "true" : "false");

      // Publish analytics event for accordion open/close
      const filterId = wrapper.dataset.filterId;
      const config = CONFIG.filters.find(function(f) { return f.id === filterId; });
      if (config) {
        const interactionType = isOpen ? 'accordion_expand' : 'accordion_collapse';
        publishPlpAnalyticsEvent('ocs:filter_interaction', buildFilterInteractionPayload(interactionType, filterCategoryLabel(config)));
      }
    });
  }

  function buildRangeSliderHTML(config) {
    const st = _state[config.id];
    const unit = config.unit || "";

    const wrapper = document.createElement("div");
    wrapper.className = "acf-filter acf-filter--slider";
    wrapper.dataset.filterId = config.id;
    wrapper.innerHTML = `
      <button type="button" class="acf-header"
              aria-expanded="${config.start_open ? "true" : "false"}"
              aria-controls="acf-body-${config.id}">${escapeHTML(config.label)}<span class="acf-filter-count"></span></button>
      <div class="acf-body" id="acf-body-${config.id}">
        <div class="acf-body__inner">
          <div class="acf-panel">
            <div class="acf-range-slider">
              <div class="acf-range-label">${fmtValue(st.currentMin, unit)} - ${fmtValue(st.currentMax, unit)}</div>
              <div class="acf-rheostat" style="position: relative;">
                <div class="acf-range-track"></div>
                <div class="acf-handle acf-handle--lower" role="slider" tabindex="0"
                     aria-label="${escapeAttr((window.themeStrings?.sliderMinHandle || "Minimum {{ filter }}").replace("{{ filter }}", config.label))}" aria-valuemin="${st.dataMin}" aria-valuemax="${st.dataMax}" aria-valuenow="${st.currentMin}"
                     style="left: 0%; position: absolute;">
                  <div class="acf-tooltip">${fmtValue(st.currentMin, unit)}</div>
                </div>
                <div class="acf-handle acf-handle--upper" role="slider" tabindex="0"
                     aria-label="${escapeAttr((window.themeStrings?.sliderMaxHandle || "Maximum {{ filter }}").replace("{{ filter }}", config.label))}" aria-valuemin="${st.dataMin}" aria-valuemax="${st.dataMax}" aria-valuenow="${st.currentMax}"
                     style="left: 100%; position: absolute;">
                  <div class="acf-tooltip">${fmtValue(st.currentMax, unit)}</div>
                </div>
                <div class="acf-range-progress" style="left: 0%; width: 100%;"></div>
                <span class="acf-range-min">${fmtValue(st.dataMin, unit)}</span>
                <span class="acf-range-max">${fmtValue(st.dataMax, unit)}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    `;
    return wrapper;
  }

  function rangeValueFromDrag(startValue, startX, clientX, dataMin, dataMax, trackWidth, step, isTouch) {
    const safeTrackWidth = Math.max(trackWidth, 1);
    const proportionalValuePerPixel = (dataMax - dataMin) / safeTrackWidth;
    const valuePerPixel = isTouch
      ? proportionalValuePerPixel
      : Math.min(proportionalValuePerPixel, step);
    const raw = startValue + (clientX - startX) * valuePerPixel;
    return dataMin + Math.round((raw - dataMin) / step) * step;
  }

  function setupRangeSlider(config, wrapper) {
    const st = _state[config.id];
    const step = config.step || 1;
    const minGap = config.min_gap || step * 2;
    const unit = config.unit || "";

    const rheostatEl = wrapper.querySelector(".acf-rheostat");
    const lowerHandle = wrapper.querySelector(".acf-handle--lower");
    const upperHandle = wrapper.querySelector(".acf-handle--upper");
    const progress = wrapper.querySelector(".acf-range-progress");
    const rangeLabel = wrapper.querySelector(".acf-range-label");
    const boundsMin = rheostatEl.querySelector(".acf-range-min");
    const boundsMax = rheostatEl.querySelector(".acf-range-max");

    function updateDOM() {
      const range = st.dataMax - st.dataMin || 1;
      const leftPct = ((st.currentMin - st.dataMin) / range) * 100;
      const rightPct = ((st.currentMax - st.dataMin) / range) * 100;
      lowerHandle.style.left = leftPct + "%";
      lowerHandle.setAttribute("aria-valuenow", st.currentMin);
      lowerHandle.setAttribute("aria-valuemin", st.dataMin);
      lowerHandle.setAttribute("aria-valuemax", st.dataMax);
      lowerHandle.setAttribute("aria-valuetext", fmtValue(st.currentMin, unit));
      lowerHandle.querySelector(".acf-tooltip").textContent = fmtValue(
        st.currentMin,
        unit,
      );
      upperHandle.style.left = rightPct + "%";
      upperHandle.setAttribute("aria-valuenow", st.currentMax);
      upperHandle.setAttribute("aria-valuemin", st.dataMin);
      upperHandle.setAttribute("aria-valuemax", st.dataMax);
      upperHandle.setAttribute("aria-valuetext", fmtValue(st.currentMax, unit));
      upperHandle.querySelector(".acf-tooltip").textContent = fmtValue(
        st.currentMax,
        unit,
      );
      progress.style.left = leftPct + "%";
      progress.style.width = rightPct - leftPct + "%";
      if (rangeLabel)
        rangeLabel.textContent =
          fmtValue(st.currentMin, unit) + " - " + fmtValue(st.currentMax, unit);
      if (boundsMin) boundsMin.textContent = fmtValue(st.dataMin, unit);
      if (boundsMax) boundsMax.textContent = fmtValue(st.dataMax, unit);
    }

    function applyFilter(handle) {
      rememberMobileFilterAnchor(handle, config);
      st.userInteracted = true;
      if (st.currentMin > st.dataMin || st.currentMax < st.dataMax) {
        rememberAppliedFilter(config.id);
      } else {
        forgetAppliedFilter(config.id);
      }
      // Prevent the dynamic_bounds update from clobbering the user's chosen max
      // when this slider's own interaction triggers the next IS result.
      st._lastSelfSearchMs = Date.now();
      clearSearchableFilterQueries(config.id);

      // FILTER (issue 4.1): one filter_event carrying the full 16-field state table.
      publishFilterEvent(filterEventField(config));

      // FILTER-UI-006: emit a min/max input update once per committed handle
      // change, with the numeric value and the filter group as filter_category.
      // THC and CBD sliders are intentionally excluded from filter_interaction.
      if (st._lastAnalyticsMin === undefined) st._lastAnalyticsMin = st.dataMin;
      if (st._lastAnalyticsMax === undefined) st._lastAnalyticsMax = st.dataMax;
      if (st.currentMin !== st._lastAnalyticsMin) {
        publishRangeFilterInteraction(config, 'min_input_update', st.currentMin);
        st._lastAnalyticsMin = st.currentMin;
      }
      if (st.currentMax !== st._lastAnalyticsMax) {
        publishRangeFilterInteraction(config, 'max_input_update', st.currentMax);
        st._lastAnalyticsMax = st.currentMax;
      }

      if (_triggerSearch) _triggerSearch();
    }

    function makeDraggable(handle, isMin) {
      let dragStartX = 0;
      let dragStartValue = 0;

      function rememberDragStart(e) {
        dragStartX = (e.touches ? e.touches[0] : e).clientX;
        dragStartValue = isMin ? st.currentMin : st.currentMax;
      }

      function onMove(e) {
        if (e.touches && e.cancelable) e.preventDefault();
        const clientX = (e.touches ? e.touches[0] : e).clientX;
        const rect = rheostatEl.getBoundingClientRect();
        const snapped = rangeValueFromDrag(
          dragStartValue,
          dragStartX,
          clientX,
          st.dataMin,
          st.dataMax,
          rect.width,
          step,
          Boolean(e.touches),
        );
        if (isMin) {
          st.currentMin = Math.max(
            st.dataMin,
            Math.min(snapped, st.currentMax - minGap),
          );
        } else {
          st.currentMax = Math.min(
            st.dataMax,
            Math.max(snapped, st.currentMin + minGap),
          );
        }
        updateDOM();
      }

      function onEnd() {
        document.removeEventListener("mousemove", onMove);
        document.removeEventListener("mouseup", onEnd);
        document.removeEventListener("touchmove", onMove);
        document.removeEventListener("touchend", onEnd);
        document.removeEventListener("touchcancel", onEnd);
        applyFilter(handle);
      }

      function bringToFront() {
        lowerHandle.style.zIndex = "2";
        upperHandle.style.zIndex = "2";
        handle.style.zIndex = "3";
      }

      handle.addEventListener("click", () => {
        if (!window.matchMedia("(hover: hover) and (pointer: fine)").matches)
          return;
        handle.focus();
        bringToFront();
      });

      handle.addEventListener("mousedown", (e) => {
        e.preventDefault();
        bringToFront();
        rememberDragStart(e);
        document.addEventListener("mousemove", onMove);
        document.addEventListener("mouseup", onEnd);
      });
      handle.addEventListener(
        "touchstart",
        (e) => {
          e.preventDefault();
          bringToFront();
          rememberDragStart(e);
          document.addEventListener("touchmove", onMove, { passive: false });
          document.addEventListener("touchend", onEnd);
          document.addEventListener("touchcancel", onEnd);
        },
        { passive: false },
      );

      handle.addEventListener("focus", bringToFront);
      handle.addEventListener("keydown", (e) => {
        if (isMin) {
          if (e.key === "ArrowRight" || e.key === "ArrowUp")
            st.currentMin = Math.min(
              st.currentMin + step,
              st.currentMax - minGap,
            );
          else if (e.key === "ArrowLeft" || e.key === "ArrowDown")
            st.currentMin = Math.max(st.currentMin - step, st.dataMin);
          else return;
        } else {
          if (e.key === "ArrowRight" || e.key === "ArrowUp")
            st.currentMax = Math.min(st.currentMax + step, st.dataMax);
          else if (e.key === "ArrowLeft" || e.key === "ArrowDown")
            st.currentMax = Math.max(
              st.currentMax - step,
              st.currentMin + minGap,
            );
          else return;
        }
        e.preventDefault();
        updateDOM();
        applyFilter(handle);
      });
    }

    makeDraggable(lowerHandle, true);
    makeDraggable(upperHandle, false);
    updateDOM();
    setupAccordionA11y(wrapper);

    // Expose so seedFacets can refresh the DOM after stats arrive
    st.updateDOM = updateDOM;
  }

  // =========================================================================
  // Toggle
  // =========================================================================

  function initToggleState(config) {
    const urlParams = new URLSearchParams(window.location.search);
    // Include-mode toggles use url_param (preferred) or attribute as the URL key.
    const urlKey = config.url_param || config.attribute;
    const seeded = urlKey
      ? urlParams.get(urlKey) === "true" || urlParams.get(urlKey) === "1"
      : false;

    // If default_active is set and the user has NOT explicitly set the URL param
    // (i.e. no navigated-with-filter state), start the toggle as active.
    const urlParamPresent = urlKey ? urlParams.has(urlKey) : false;
    const defaultActive = !urlParamPresent && !!config.default_active;

    _state[config.id] = {
      type: "toggle",
      config,
      active: seeded || defaultActive,
    };
  }

  /**
   * Build the full accordion wrapper for a toggle filter. The wrapper uses
   * config.label as a group key so consecutive toggles with the same header
   * can be merged into one accordion (see injectFilters).
   */
  function buildToggleHTML(config) {
    const wrapper = document.createElement("div");
    wrapper.className = "acf-filter acf-filter--toggle";
    wrapper.dataset.toggleGroup = config.label;
    if (config.start_open) wrapper.classList.add("is-open");
    wrapper.innerHTML = `
      <button type="button" class="acf-header"
              aria-expanded="${config.start_open ? "true" : "false"}"
              aria-controls="acf-body-toggle-${config.id}">${escapeHTML(config.label)}<span class="acf-filter-count"></span></button>
      <div class="acf-body" id="acf-body-toggle-${config.id}">
        <div class="acf-body__inner">
          <div class="acf-panel">
            <div class="acf-refinement">
              <ul class="acf-list"></ul>
            </div>
          </div>
        </div>
      </div>
    `;
    return wrapper;
  }

  /**
   * Build a single <li> checkbox item for a toggle and append it to the
   * accordion wrapper's <ul>.
   */
  function appendToggleItem(config, wrapper) {
    const st = _state[config.id];
    const ul = wrapper.querySelector(".acf-list");
    const li = document.createElement("li");
    li.className = "acf-item" + (st.active ? " acf-item--selected" : "");
    li.dataset.filterId = config.id;
    li.innerHTML = `
      <div>
        <label class="acf-label">
          <input type="checkbox" class="acf-checkbox acf-toggle-checkbox" ${st.active ? "checked" : ""}>
          ${escapeHTML(config.filter_label || config.label)}<span class="acf-count acf-toggle-count"></span>
        </label>
      </div>
    `;
    ul.appendChild(li);
  }

  function setupToggle(config, wrapper) {
    const st = _state[config.id];
    const li = wrapper.querySelector(
      '.acf-item[data-filter-id="' + config.id + '"]',
    );
    const checkbox = li.querySelector(".acf-toggle-checkbox");

    checkbox.addEventListener("change", () => {
      if (_inPopstate) return;
      rememberMobileFilterAnchor(checkbox, config);
      history.pushState(null, '', window.location.href);
      st.active = checkbox.checked;
      if (st.active) {
        rememberAppliedFilter(config.id);
      } else {
        forgetAppliedFilter(config.id);
      }
      li.classList.toggle("acf-item--selected", st.active);
      _syncToggleState();
      clearSearchableFilterQueries(config.id);

      // FILTER / FILTER-REMOVE: one event with the full 16-field state table.
      if (st.active) {
        publishFilterEvent(filterEventField(config));
      } else {
        publishFilterRemove(filterEventField(config));
      }

      if (_triggerSearch) _triggerSearch();
    });
    setupAccordionA11y(wrapper);
  }

  /**
   * Directly writes active toggle url_params into the browser URL via replaceState.
   * IS routing only serializes its own known state keys, so custom toggle params
   * must be managed independently. Always uses replaceState so it amends the current
   * history entry rather than creating a new one. The real history entry for each
   * toggle interaction is created by IS's historyRouter calling pushState when it
   * detects that the URL has changed (IS's shouldWrite guard). This function is called
   * by _installToggleUrlInterceptor after every IS history write so toggle params are
   * never stripped by IS's URL rewrites.
   */
  function _writeToggleUrlParams() {
    const params = new URLSearchParams(window.location.search);
    Object.values(_state).forEach(function (st) {
      if (st.type !== "toggle") return;
      const key = st.config.url_param || st.config.attribute;
      if (!key) return;
      if (st.active) {
        params.set(key, "true");
      } else {
        params.delete(key);
      }
    });
    const search = params.toString();
    history.replaceState(null, '', (search ? '?' + search : window.location.pathname) + window.location.hash);
  }

  /**
   * Intercepts history.replaceState and history.pushState so that toggle URL params
   * (e.g., ?hide_oos=true) survive IS's debounced router writes.
   *
   * IS's historyRouter debounces URL updates (~400ms) and writes the full URL,
   * stripping any params that aren't part of IS's own uiState. The interceptor
   * re-appends toggle params immediately after each history write via replaceState.
   *
   * For toggle changes specifically, this interceptor is what creates the correct
   * back-navigation behaviour. When a toggle fires:
   *   1. _triggerSearch() -> is.setUiState() -> IS detects a URL change -> IS calls
   *      history.pushState (new entry, old URL -> new URL with toggle param).
   *   2. Our pushState interceptor fires -> calls _writeToggleUrlParams() via replaceState
   *      -> appends any toggle params not already in IS's URL.
   * Net result: exactly 1 history entry per toggle interaction, with the correct
   * post-toggle URL. Back button restores the pre-toggle URL. IS's shouldWrite guard
   * is satisfied because the URL before the toggle interaction differs from the new URL.
   *
   * A reentrancy guard (_writing) prevents the replaceState call inside
   * _writeToggleUrlParams from retriggering the interceptor infinitely.
   */
  function _installToggleUrlInterceptor() {
    let _writing = false;

    function _patch(method) {
      const orig = history[method].bind(history);
      history[method] = function (state, title, url) {
        orig(state, title, url);
        if (_writing) return;
        if (_inPopstate) return;
        // Only act when there are toggles with a url key to preserve
        const needsWrite = Object.values(_state).some(function (st) {
          if (st.type !== "toggle") return false;
          const key = st.config.url_param || st.config.attribute;
          return !!key;
        });
        if (!needsWrite) return;
        _writing = true;
        _writeToggleUrlParams();
        _writing = false;
      };
    }

    _patch("replaceState");
    _patch("pushState");
  }

  /**
   * Syncs active toggle labels for analytics and locale-independent URL parameter
   * keys for external renderers such as the product-card swatch builder.
   */
  function _syncToggleState() {
    const activeToggles = Object.values(_state)
      .filter(function (st) { return st.type === "toggle" && st.active; });
    window._acfActiveToggles = activeToggles
      .map(function (st) { return st.config.filter_label || ""; });
    window._acfActiveToggleParams = activeToggles
      .map(function (st) { return st.config.url_param || ""; })
      .filter(Boolean);
    document.dispatchEvent(new CustomEvent("acf:toggles-changed"));
  }

  // =========================================================================
  // Refinement List
  // =========================================================================

  function initRefinementListState(config) {
    let selected = new Set();
    // Parse value_map / bool_labels early so they're available before the first render
    if (config.value_map && !config._parsedValueMap) {
      config._parsedValueMap = parseValueMap(config.value_map);
    }
    if (config.bool_labels && !config._parsedBoolLabels) {
      config._parsedBoolLabels = parseBoolLabels(config.bool_labels);
    }

    // Seed selected values from URL for non-native-disjunctive filters (tag_list, boolean_list,
    // conjunctive refinement_list). Native disjunctive lists are seeded later in
    // _acfGetDisjunctiveSearchParams via IS routing uiState.
    if (!isNativeDisjunctive(config) && config.url_param) {
      const urlParams = new URLSearchParams(window.location.search);
      const raw = urlParams.get(config.url_param);
      if (raw) {
        raw.split(",").forEach(function (v) {
          const t = decodeURIComponent(v.trim());
          if (t) selected.add(t);
        });
      }
    }

    _state[config.id] = {
      type: config.type,
      config,
      facetValues: [],
      selected,
      _prevSelected: new Set(selected), // seeded so URL-restored values can be removed on first interaction
      valueAttrMap: new Map(),
      searchQuery: "",
    };
  }

  function formatPriceRangeLabel(raw) {
    const parts = raw.split(":");
    if (parts.length !== 2) return raw;
    const lo = parseInt(parts[0], 10);
    const hi = parseInt(parts[1], 10);
    if (isNaN(lo) || isNaN(hi)) return raw;

    const locale = window.Shopify?.locale || document.documentElement?.lang || "en";
    if (!String(locale).toLowerCase().startsWith("fr") || !window.OCSMoney?.formatMajor) {
      if (lo === 0) return "Under $" + hi;
      return "$" + lo + " - $" + hi;
    }

    const labels = window.algoliaCustomFilters?.price_labels || {};
    const minimum = window.OCSMoney.formatMajor(lo);
    const maximum = window.OCSMoney.formatMajor(hi);
    if (lo === 0 && labels.under) return labels.under.replace("$__MAX__", maximum);
    if (labels.range) {
      return labels.range.replace("$__MIN__", minimum).replace("$__MAX__", maximum);
    }
    return `${minimum} - ${maximum}`;
  }

  function parseValueMap(raw) {
    if (!raw) return null;
    const map = {};
    raw.split("\n").forEach(function (line) {
      line = line.trim();
      if (!line) return;
      const idx = line.indexOf(":");
      if (idx === -1) {
        map[line] = line;
        return;
      }
      const key = line.slice(0, idx).trim();
      const label = line.slice(idx + 1).trim();
      if (key) map[key] = label || key;
    });
    return Object.keys(map).length ? map : null;
  }

  function getDisplayValue(config, value) {
    if (config._parsedBoolLabels && config._parsedBoolLabels[value])
      return config._parsedBoolLabels[value];
    if (config._parsedValueMap && config._parsedValueMap[value])
      return config._parsedValueMap[value];
    const prefix = config.prefix || "";
    if (prefix) return value.slice(prefix.length);
    if (config.attribute === "price_range") return formatPriceRangeLabel(value);
    return value;
  }

  function applyRefinementListFacets(config, facets) {
    const st = _state[config.id];
    if (!st || !facets) return;

    // Bool-labels mode: each attribute maps to one labelled option (e.g. boolean metafields).
    // The "value" stored in selected is the attribute name; filter emits attr:"true".
    const boolMap = config._parsedBoolLabels;
    if (boolMap) {
      const newValues = Object.entries(boolMap)
        .map(([attr, label]) => {
          const attrFacets = facets[attr];
          const count =
            (attrFacets && (attrFacets["true"] || attrFacets["1"] || 0)) || 0;
          st.valueAttrMap.set(attr, attr);
          return { value: attr, displayValue: label, count };
        })
        .filter((item) => item.count > 0 || st.selected.has(item.value));
      st.selected.forEach((val) => {
        if (!newValues.find((f) => f.value === val)) {
          newValues.push({
            value: val,
            displayValue: boolMap[val] || val,
            count: 0,
          });
        }
      });
      newValues.sort((a, b) =>
        (a.displayValue || a.value).toLowerCase() <
        (b.displayValue || b.value).toLowerCase()
          ? -1
          : 1,
      );
      st.facetValues = newValues;
      return;
    }

    const attrs = getAttrs(config);
    const prefix = config.prefix || "";
    if (!config._parsedValueMap && config.value_map) {
      config._parsedValueMap = parseValueMap(config.value_map);
    }
    const vmap = config._parsedValueMap;

    // Merge facet values from all attributes (handles both single and multi-attribute)
    const merged = {}; // value -> {count, attr}
    attrs.forEach(function (attr) {
      const attrFacets = facets[attr];
      if (!attrFacets) return;
      Object.entries(attrFacets).forEach(function ([value, count]) {
        if (merged[value]) {
          merged[value].count += count;
        } else {
          merged[value] = { count, attr };
        }
      });
    });

    const filtered = Object.entries(merged).filter(([value]) => {
      if (vmap) return vmap.hasOwnProperty(value);
      return !prefix || value.startsWith(prefix);
    });
    const newValues = filtered.map(([value, { count, attr }]) => {
      st.valueAttrMap.set(value, attr);
      return { value, displayValue: getDisplayValue(config, value), count };
    });

    // Always keep selected values visible even if Algolia doesn't return them
    // in the current facets response (e.g. filtered-out conjunctive values).
    st.selected.forEach(function (val) {
      if (
        !newValues.find(function (f) {
          return f.value === val;
        })
      ) {
        newValues.push({
          value: val,
          displayValue: getDisplayValue(config, val),
          count: 0,
        });
      }
    });

    // Natural sort: splits each string into alternating text/number segments,
    // comparing numeric segments as numbers so "2g" < "14g" instead of "14g" < "2g".
    function naturalCompare(a, b) {
      const re = /(\d+\.?\d*|\.\d+)/g;
      const aParts = a.toLowerCase().split(re);
      const bParts = b.toLowerCase().split(re);
      for (let i = 0; i < Math.max(aParts.length, bParts.length); i++) {
        const aSeg = aParts[i] ?? '';
        const bSeg = bParts[i] ?? '';
        if (aSeg === bSeg) continue;
        const aNum = parseFloat(aSeg);
        const bNum = parseFloat(bSeg);
        if (!isNaN(aNum) && !isNaN(bNum)) return aNum - bNum;
        return aSeg < bSeg ? -1 : 1;
      }
      return 0;
    }

    // [ACF DEBUG]: values about to be rendered for this filter panel
    if (config.attribute === 'meta.product.brand') {
      console.log('[ACF DEBUG] Brand filter values:', newValues.map(v => v.value + '(' + v.count + ')'));
    }

    newValues.sort(function (a, b) {
      // Sort price ranges by lower bound numerically
      if (config.attribute === "price_range") {
        const aLo = parseInt(a.value.split(":")[0], 10) || 0;
        const bLo = parseInt(b.value.split(":")[0], 10) || 0;
        return aLo - bLo;
      }
      // Natural sort: handles mixed text/number labels like "1g", "2g", "14g", "28g"
      const aLabel = a.displayValue || a.value;
      const bLabel = b.displayValue || b.value;
      return naturalCompare(aLabel, bLabel);
    });

    st.facetValues = newValues;
  }

  function buildRefinementListHTML(config) {
    const wrapper = document.createElement("div");
    wrapper.className = "acf-filter acf-filter--refinement";
    wrapper.dataset.filterId = config.id;

    const searchLabel = escapeAttr(
      (window.themeStrings?.searchFilter || "Search {{ filter }}").replace(
        "{{ filter }}",
        config.label,
      ),
    );
    const searchBoxHTML = config.searchable
      ? `
            <div class="acf-search-box">
              <form action="" role="search" class="acf-search-form" novalidate>
                <button class="acf-search-submit" type="submit" aria-label="${searchLabel}">
                <svg width="30" height="30" viewBox="0 0 30 30" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false">
                <path d="M21.2462 14.2874C21.3144 10.3761 18.199 7.15006 14.2877 7.08179C10.3765 7.01351 7.15038 10.1289 7.08211 14.0401C7.01384 17.9514 10.1292 21.1774 14.0405 21.2457C17.9518 21.314 21.1779 18.1986 21.2462 14.2874Z" stroke="#727272"/>
                <path d="M19.3589 19.3711L22.912 22.9242" stroke="#727272" stroke-linecap="square"/>
                </svg>
                </button>
                <input class="acf-search-input" type="search"
                       placeholder="${searchLabel}"
                       autocomplete="off" autocorrect="off" autocapitalize="off" spellcheck="false"
                       maxlength="512" aria-label="${searchLabel}">
              </form>
            </div>`
      : "";

    const subtitleHTML = config.subtitle
      ? `<p class="acf-subtitle">${escapeHTML(config.subtitle)}</p>`
      : "";

    if (config.start_open) wrapper.classList.add("is-open");
    wrapper.innerHTML = `
      <button type="button" class="acf-header"
              aria-expanded="${config.start_open ? "true" : "false"}"
              aria-controls="acf-body-refine-${config.id}">${escapeHTML(config.label)}<span class="acf-filter-count"></span></button>
      <div class="acf-body" id="acf-body-refine-${config.id}">
        <div class="acf-body__inner">
          ${subtitleHTML}
          <div class="acf-panel">
            <div class="acf-refinement">
              ${searchBoxHTML}
              <ul class="acf-list"></ul>
              <button class="acf-show-more acf-show-more--disabled" disabled aria-expanded="false" aria-controls="acf-body-refine-${config.id}">
                <span data-showing-more="false">View more</span>
              </button>
            </div>
          </div>
        </div>
      </div>
    `;
    return wrapper;
  }

  const REFINEMENT_LIMIT = 6;

  function shouldShowRefinementExpansion(optionCount) {
    // Seven options still fit comfortably without an extra interaction. Collapse
    // to the six-option preview only when at least eight options are available.
    return optionCount > REFINEMENT_LIMIT + 1;
  }

  function getFilterWrapper(filterId) {
    return document.querySelector(
      `.acf-filter[data-filter-id="${CSS.escape(filterId)}"]`,
    );
  }

  function resetSearchableFilterQuery(filterId) {
    const st = _state[filterId];
    if (!st || !st.config || !st.config.searchable || !st.searchQuery) return;

    st.searchQuery = "";

    const wrapper = getFilterWrapper(filterId);
    if (!wrapper) return;

    const input = wrapper.querySelector(".acf-search-input");
    if (input) input.value = "";

    renderRefinementListItems(st.config, wrapper);
  }

  function clearSearchableFilterQueries(exceptFilterId) {
    Object.keys(_state).forEach(function (filterId) {
      if (filterId === exceptFilterId) return;
      resetSearchableFilterQuery(filterId);
    });
  }

  function refinementDisplayValue(config, value) {
    const display = String(value || "");
    if (config.attribute === "meta.product.allergens") {
      // Algolia stores allergen facets in uppercase. Lowercase the source text;
      // .acf-label's capitalize transform then presents each word in title case.
      return display.toLocaleLowerCase();
    }
    return display;
  }

  function renderRefinementListItems(config, wrapper) {
    const st = _state[config.id];
    const list = wrapper.querySelector(".acf-list");
    if (!list) return;

    const query = st.searchQuery.toLowerCase();
    const filtered = query
      ? st.facetValues.filter((f) =>
          (f.displayValue || f.value).toLowerCase().includes(query),
        )
      : st.facetValues;

    // No facet data: either still loading or attribute not in Algolia
    if (st.facetValues.length === 0 && st.selected.size === 0) {
      if (_resultsReceived) {
        // Results arrived but this attribute returned no data: hide the filter
        wrapper.style.display = "none";
        if (!st._noDataWarned) {
          st._noDataWarned = true;
          // console.warn(
          //   '[acf] Filter "' +
          //     config.label +
          //     '" hidden: attribute not returned by Algolia. Check that "' +
          //     (config.attribute || config.bool_labels || "") +
          //     '" is in attributesForFaceting.',
          // );
        }
      } else {
        // Still loading: show skeleton rows
        list.innerHTML = Array.from(
          { length: 4 },
          () =>
            `<li class="acf-item acf-item--skeleton">
            <div class="acf-skeleton-line acf-skeleton-filter-item"></div>
          </li>`,
        ).join("");
      }
      return;
    }

    // Has data: ensure visible (may have been hidden on a previous empty result)
    wrapper.style.display = "";

    // Search query typed but zero values matched: show inline empty state
    if (query && filtered.length === 0) {
      const msg = (window.T?.filterNoResults?.()) ||
        'No Results';
      list.innerHTML = `<li class="acf-item acf-item--no-results">${msg}</li>`;
      // Also disable the show-more button: nothing to show
      const btn = wrapper.querySelector(".acf-show-more");
      if (btn) {
        btn.classList.add("acf-show-more--disabled");
        btn.disabled = true;
      }
      return;
    }

    const showingMore = st._showingMore || false;
    const needsExpansion = shouldShowRefinementExpansion(filtered.length);
    const visible = showingMore || !needsExpansion
      ? filtered
      : filtered.slice(0, REFINEMENT_LIMIT);

    list.innerHTML = visible
      .map((f) => {
        const isSelected = st.selected.has(f.value);
        const display = refinementDisplayValue(config, f.displayValue || f.value);
        return `
        <li class="acf-item${isSelected ? " acf-item--selected" : ""}">
          <div>
            <label class="acf-label">
              <input type="checkbox" class="acf-checkbox acf-refine-checkbox" value="${escapeAttr(f.value)}" ${isSelected ? "checked" : ""}>
              ${escapeHTML(display)}<span class="acf-count">${formatCount(f.count)}</span>
            </label>
          </div>
        </li>`;
      })
      .join("");

    // Show more / less button
    const btn = wrapper.querySelector(".acf-show-more");
    if (btn) {
      if (needsExpansion) {
        btn.classList.remove("acf-show-more--disabled");
        btn.disabled = false;
        btn.setAttribute("aria-expanded", showingMore ? "true" : "false");
        const span = btn.querySelector("span");
        if (span) {
          span.dataset.showingMore = showingMore ? "true" : "false";
          span.textContent = showingMore
            ? window.themeStrings?.viewLess || "View less"
            : window.themeStrings?.viewMore || "View more";
        }
      } else {
        btn.classList.add("acf-show-more--disabled");
        btn.disabled = true;
        btn.setAttribute("aria-expanded", "false");
      }
    }
  }

  function setupRefinementList(config, wrapper) {
    const st = _state[config.id];
    renderRefinementListItems(config, wrapper);

    wrapper.addEventListener("change", (e) => {
      if (!e.target.classList.contains("acf-refine-checkbox")) return;
      rememberMobileFilterAnchor(e.target, config);
      const viewportSnapshot = captureMobileFilterViewport();
      const val = e.target.value;
      const isChecked = e.target.checked;
      if (isChecked) {
        st.selected.add(val);
        rememberAppliedFilter(config.id, val);
      } else {
        st.selected.delete(val);
        forgetAppliedFilter(config.id, val);
      }
      renderRefinementListItems(config, wrapper);
      clearSearchableFilterQueries(config.id);
      // Restore the interacted option and its viewport position after the list rebuild.
      // Deferring past mouseup prevents the browser from overriding the restored focus.
      setTimeout(() => {
        if (viewportSnapshot) {
          restoreMobileFilterViewport(viewportSnapshot);
          return;
        }
        const refocused = wrapper.querySelector(
          `.acf-refine-checkbox[value="${CSS.escape(val)}"]`,
        );
        if (refocused) refocused.focus();
      }, 0);

      // FILTER / FILTER-REMOVE: one event with the full 16-field state table.
      if (isChecked) {
        publishFilterEvent(filterEventField(config));
      } else {
        publishFilterRemove(filterEventField(config));
      }

      if (_triggerSearch) _triggerSearch();
    });

    // Show more / less toggle
    const showMoreBtn = wrapper.querySelector(".acf-show-more");
    if (showMoreBtn) {
      showMoreBtn.addEventListener("click", function () {
        st._showingMore = !st._showingMore;
        renderRefinementListItems(config, wrapper);

        // Publish analytics event for view more/less
        const interactionType = st._showingMore ? 'view_more' : 'view_less';
        publishPlpAnalyticsEvent('ocs:filter_interaction', buildFilterInteractionPayload(interactionType, filterCategoryLabel(config)));
      });
    }

    if (config.searchable) {
      const searchInput = wrapper.querySelector(".acf-search-input");
      if (searchInput) {
        searchInput.addEventListener("input", () => {
          st.searchQuery = searchInput.value;
          renderRefinementListItems(config, wrapper);
        });

        const form = wrapper.querySelector(".acf-search-form");
        if (form) {
          form.addEventListener("submit", (e) => {
            e.preventDefault();
            if (!searchInput.value.trim()) return;

            // FILTER-UI-005: one committed search action publishes filter_search
            // with the filter group as filter_category. The free-form query text
            // is never included.
            publishPlpAnalyticsEvent('ocs:filter_interaction', buildFilterInteractionPayload('filter_search', filterCategoryLabel(config)));
          });
        }
      }
    }
    setupAccordionA11y(wrapper);
  }

  // =========================================================================
  // Shared filter string hook
  // =========================================================================

  const IN_STORE_FILTER = 'NOT meta.variant.availability:"in-store"';

  function _isInStoreToggleActive() {
    return Object.values(_state).some(function (st) {
      return st.type === "toggle" && st.active && st.config.url_param === "show_in_store";
    });
  }

  function buildFilterString() {
    const parts = [];
    const inStoreCfg = CONFIG.filters.find(function(f) { return f.url_param === 'show_in_store'; });
    if (inStoreCfg && !_isInStoreToggleActive()) parts.push(IN_STORE_FILTER);
    for (const id of Object.keys(_state)) {
      const st = _state[id];
      if (st.type === "range_slider") {
        const cfg = st.config;
        if (st.currentMin > st.dataMin || st.currentMax < st.dataMax) {
          const fp = [];
          const maxAttr = getEffectiveMaxAttr(cfg);
          if (cfg._facetOverride) {
            fp.push(`${maxAttr}>=${st.currentMin}`);
          } else if (isMinAttributeEnabled(cfg)) {
            fp.push(`${cfg.min_attribute}>=${st.currentMin}`);
          }
          fp.push(`${maxAttr}<=${st.currentMax}`);
          parts.push(fp.join(" AND "));
        }
      }
      if (st.type === "toggle") {
        const cfg = st.config;
        if (cfg.mode === "exclude") {
          // Exclude-mode: filter is active by default, removed when user checks the box
          if (!st.active && cfg.exclude_filter) {
            parts.push(cfg.exclude_filter);
          }
        } else {
          // Include-mode: filter added when user checks the box.
          // Supports either a raw include_filter string or attribute/operator/value.
          if (st.active) {
            const f = cfg.include_filter || (cfg.attribute ? `${cfg.attribute}${cfg.operator}${cfg.value}` : null);
            if (f) parts.push(f);
          }
        }
      }
      if (
        (st.type === "refinement_list" ||
          st.type === "boolean_list" ||
          st.type === "tag_list") &&
        st.selected.size > 0
      ) {
        const cfg = st.config;
        // Exclude mode: emit NOT attr:"value" AND NOT attr:"value" for each checked item.
        // The NOT filter string is built here; IS never sees these as facetFilters.
        if (cfg.exclude_mode && st.selected.size > 0) {
          const attrs = getAttrs(cfg);
          const notClauses = [...st.selected].map((v) => {
            const knownAttr = st.valueAttrMap && st.valueAttrMap.get(v);
            if (knownAttr) return `NOT ${knownAttr}:"${v}"`;
            if (attrs.length === 1) return `NOT ${attrs[0]}:"${v}"`;
            return `NOT (${attrs.map((a) => `${a}:"${v}"`).join(' OR ')})`;
          });
          parts.push(notClauses.join(' AND '));
          continue;
        }
        // Native disjunctive refinement lists normally use IS facetFilters. If any
        // selected value has reserved Algolia syntax, route the entire selection as
        // one quoted OR group instead. Splitting normal and quoted values between
        // facetFilters and filters would AND the two groups and return zero results.
        // Boolean-list options are separate attributes, so they also stay in this
        // filters string where operator_type can combine them as one OR/AND group.
        if (isNativeDisjunctive(cfg)) {
          const selectedValues = [...st.selected];
          if (!selectedValues.some(_needsQuoting)) continue;
          const attrs = getAttrs(cfg);
          const vals = selectedValues.map((v) => {
            const quotedValue = _quoteAlgoliaFilterValue(v);
            const knownAttr = st.valueAttrMap && st.valueAttrMap.get(v);
            if (knownAttr) return `${knownAttr}:${quotedValue}`;
            return "(" + attrs.map((a) => `${a}:${quotedValue}`).join(" OR ") + ")";
          });
          parts.push("(" + vals.join(" OR ") + ")");
          continue;
        }
        let vals;
        if (cfg._parsedBoolLabels) {
          vals = [...st.selected].map((v) => `${v}:"true"`);
        } else {
          const attrs = getAttrs(cfg);
          vals = [...st.selected].map((v) => {
            const knownAttr = st.valueAttrMap && st.valueAttrMap.get(v);
            if (knownAttr) return `${knownAttr}:"${v}"`;
            return "(" + attrs.map((a) => `${a}:"${v}"`).join(" OR ") + ")";
          });
        }
        if (cfg.operator_type === "disjunctive") {
          parts.push("(" + vals.join(" OR ") + ")");
        } else {
          parts.push(vals.join(" AND "));
        }
      }
    }
    return parts.length ? parts.join(" AND ") : "";
  }

  // =========================================================================
  // Active Filters Bar
  // =========================================================================

  const CLOSE_SVG =
    '<svg width="10" height="10" viewBox="0 0 10 10" aria-hidden="true"><path d="M1 1l8 8M9 1l-8 8" stroke="currentColor" stroke-width="1.5" fill="none"/></svg>';

  function getActiveFilters() {
    const active = [];
    for (const id of Object.keys(_state)) {
      const st = _state[id];
      const cfg = st.config;
      if (
        st.type === "range_slider" &&
        (st.currentMin > st.dataMin || st.currentMax < st.dataMax)
      ) {
        const unit = cfg.unit || "";
        active.push({
          id,
          type: "range_slider",
          label:
            cfg.label +
            ": " +
            fmtValue(st.currentMin, unit) +
            " – " +
            fmtValue(st.currentMax, unit),
        });
      }
      if (st.type === "toggle" && st.active) {
        active.push({
          id,
          type: "toggle",
          label: cfg.filter_label || cfg.label,
        });
      }
      if (
        (st.type === "refinement_list" ||
          st.type === "boolean_list" ||
          st.type === "tag_list") &&
        st.selected.size > 0
      ) {
        st.selected.forEach((val) => {
          const facet = st.facetValues.find((f) => f.value === val);
          const displayLabel = refinementDisplayValue(
            cfg,
            (facet && facet.displayValue) || getDisplayValue(cfg, val),
          );
          active.push({ id, type: st.type, label: displayLabel, value: val });
        });
      }
    }
    return sortActiveFiltersByAppliedOrder(active);
  }

  function clearFilter(filterId, value) {
    const _cfSt = _state[filterId];
    if (_cfSt && _cfSt.type === 'toggle' && !_inPopstate) {
      history.pushState(null, '', window.location.href);
    }
    const st = _state[filterId];
    if (!st) return;
    const cfg = st.config;

    // Capture removed value before clearing for analytics
    const removedValue = value || (st.type === 'toggle' && st.active ? 'true' : null) || (st.type === 'range_slider' ? st.currentMin + '-' + st.currentMax : null);
    if (st.type === "range_slider") {
      forgetAppliedFilter(filterId);
      st.currentMin = st.dataMin;
      st.currentMax = st.dataMax;
      if (cfg.dynamic_bounds) {
        st._lastSelfSearchMs = null;
        st.userInteracted = false;
      }
      if (st.updateDOM) st.updateDOM();
    }
    if (st.type === "toggle") {
      forgetAppliedFilter(filterId);
      st.active = false;
      // Toggle items live on <li data-filter-id="..."> inside grouped accordions
      const li = document.querySelector(
        '.acf-item[data-filter-id="' + filterId + '"]',
      );
      if (li) {
        const checkbox = li.querySelector(".acf-toggle-checkbox");
        if (checkbox) checkbox.checked = false;
        li.classList.remove("acf-item--selected");
      }
    }
    if (
      st.type === "refinement_list" ||
      st.type === "boolean_list" ||
      st.type === "tag_list"
    ) {
      if (value) {
        forgetAppliedFilter(filterId, value);
        st.selected.delete(value);
      } else {
        st.selected.forEach((selectedValue) => {
          forgetAppliedFilter(filterId, selectedValue);
        });
        st.selected.clear();
      }
      const w = document.querySelector(
        '.acf-filter[data-filter-id="' + filterId + '"]',
      );
      if (w) renderRefinementListItems(cfg, w);
    }
    clearSearchableFilterQueries();
    _syncToggleState();

    // FILTER-REMOVE: chip removal emits the same filter_removed 16-field event.
    if (removedValue) {
      publishFilterRemove(filterEventField(cfg));
    }

    if (_triggerSearch) _triggerSearch();
    _writeToggleUrlParams();
    renderActiveFiltersBar();
  }

  function clearAllFilters() {
    // FILTER-UI-004: clear-all is a UI interaction; the FILTER-REMOVE filter_removed
    // event is published after the reset below so its 16-field snapshot reflects
    // the now-empty state.
    const hadActiveFilters = getActiveFilters().length > 0;
    if (hadActiveFilters) {
      publishPlpAnalyticsEvent('ocs:filter_interaction', buildFilterInteractionPayload('clear_all', 'All Filters'));
    }

    _appliedFilterOrder.length = 0;
    for (const id of Object.keys(_state)) {
      const st = _state[id];
      const cfg = st.config;
      if (st.type === "range_slider") {
        st.currentMin = st.dataMin;
        st.currentMax = st.dataMax;
        if (cfg.dynamic_bounds) {
          // Clear the timestamp guard so refreshDynamicSliderBounds fires on the
          // next IS result and can expand dataMax back to the unfiltered ceiling.
          st._lastSelfSearchMs = null;
          // Mark as not user-interacted so the dynamic bounds update sets
          // currentMax = newDataMax instead of leaving it at the old locked value.
          st.userInteracted = false;
        }
        if (st.updateDOM) st.updateDOM();
      }
      if (st.type === "toggle") {
        st.active = false;
        const li = document.querySelector(
          '.acf-item[data-filter-id="' + id + '"]',
        );
        if (li) {
          const checkbox = li.querySelector(".acf-toggle-checkbox");
          if (checkbox) checkbox.checked = false;
          li.classList.remove("acf-item--selected");
        }
      }
      if (
        st.type === "refinement_list" ||
        st.type === "boolean_list" ||
        st.type === "tag_list"
      ) {
        st.selected.clear();
        const w = document.querySelector(
          '.acf-filter[data-filter-id="' + id + '"]',
        );
        if (w) renderRefinementListItems(cfg, w);
      }
    }
    clearSearchableFilterQueries();

    // Collapse all open filter accordions and sync aria-expanded
    document
      .querySelectorAll(".acf-filter.is-open")
      .forEach((filter) => {
        filter.classList.remove("is-open");
        const btn = filter.querySelector(":scope > .acf-header");
        if (btn) btn.setAttribute("aria-expanded", "false");
      });

    _syncToggleState();
    if (_triggerSearch) _triggerSearch();
    _writeToggleUrlParams();
    renderActiveFiltersBar();

    // FILTER-REMOVE: after state is cleared, one filter_removed reflects the
    // now-empty snapshot (all "No Selection") with filters_removed "All Filters".
    if (hadActiveFilters) {
      publishFilterRemove('All Filters');
    }
  }

  function renderActiveFiltersBar() {
    const bar = document.querySelector(".acf-active-bar");
    if (!bar) return;
    const viewportSnapshot = captureMobileFilterViewport();
    const active = getActiveFilters();
    // Update per-filter counts
    const countsByFilter = {};
    active.forEach((f) => {
      countsByFilter[f.id] = (countsByFilter[f.id] || 0) + 1;
    });
    document
      .querySelectorAll(".acf-filter[data-filter-id]")
      .forEach((wrapper) => {
        const id = wrapper.dataset.filterId;
        const countEl = wrapper.querySelector(".acf-filter-count");
        if (countEl)
          countEl.textContent = countsByFilter[id]
            ? " (" + countsByFilter[id] + ")"
            : "";
      });
    // Toggle groups: count active toggles across all items in the group
    document
      .querySelectorAll(".acf-filter[data-toggle-group]")
      .forEach((wrapper) => {
        let groupCount = 0;
        wrapper
          .querySelectorAll(".acf-item[data-filter-id]")
          .forEach((li) => {
            groupCount += countsByFilter[li.dataset.filterId] || 0;
          });
        const countEl = wrapper.querySelector(".acf-filter-count");
        if (countEl)
          countEl.textContent = groupCount ? " (" + groupCount + ")" : "";
      });
    // Update mobile toggle active state + filter count in label
    const mobileBtn = document.querySelector(".acf-mobile-toggle");
    if (mobileBtn) {
      mobileBtn.classList.toggle("acf-mobile-toggle--active-filters", active.length > 0);
      const titleEl = mobileBtn.querySelector(".acf-mobile-toggle-title");
      if (titleEl) {
        const base = window.themeStrings?.filters || "Filter";
        titleEl.textContent = active.length > 0 ? base + " (" + active.length + ")" : base;
      }
    }
    // --- Desktop active bar ---
    if (!active.length) {
      bar.style.display = "none";
      bar.innerHTML = "";
    } else {
      bar.style.display = "";
      bar.innerHTML = "";

      const clearBtn = document.createElement("button");
      clearBtn.className = "acf-active-clear";
      clearBtn.textContent = window.themeStrings?.clearAll || "Clear all";
      clearBtn.addEventListener("click", clearAllFilters);
      bar.appendChild(clearBtn);

      active.forEach((f) => {
        const chip = document.createElement("button");
        chip.className = "acf-active-chip";
        chip.setAttribute(
          "aria-label",
          (
            window.themeStrings?.removeFilter || "Remove filter: {{ filter }}"
          ).replace("{{ filter }}", f.label),
        );
        chip.innerHTML =
          "<span>" + escapeHTML(f.label) + "</span>" + CLOSE_SVG;
        chip.addEventListener("click", () => clearFilter(f.id, f.value));
        bar.appendChild(chip);
      });
    }

    // --- Inline applied filters bar (inside acf-sidebar-header, mobile sticky) ---
    const inlineHeader = document.querySelector(".acf-inline-active-header");
    const inlineChips = document.querySelector(".acf-inline-active-chips");
    if (inlineHeader && inlineChips) {
      if (!active.length) {
        inlineHeader.style.display = "none";
        inlineChips.style.display = "none";
        inlineHeader.innerHTML = "";
        inlineChips.innerHTML = "";
      } else {
        inlineHeader.style.display = "";
        inlineChips.style.display = "";

        // Row 2: "Applied Filters (N)" + "CLEAR ALL"
        inlineHeader.innerHTML = "";
        const appliedTitle = document.createElement("span");
        appliedTitle.className = "acf-inline-active-title";
        appliedTitle.textContent = "Applied Filters (" + active.length + ")";
        const inlineClearBtn = document.createElement("button");
        inlineClearBtn.className = "acf-active-clear";
        inlineClearBtn.textContent = window.themeStrings?.clearAll || "Clear all";
        inlineClearBtn.addEventListener("click", clearAllFilters);
        inlineHeader.appendChild(appliedTitle);
        inlineHeader.appendChild(inlineClearBtn);

        // Row 3: chips: same markup as desktop bar
        inlineChips.innerHTML = "";
        active.forEach((f) => {
          const chip = document.createElement("button");
          chip.className = "acf-active-chip";
          chip.setAttribute(
            "aria-label",
            (
              window.themeStrings?.removeFilter || "Remove filter: {{ filter }}"
            ).replace("{{ filter }}", f.label),
          );
          chip.innerHTML =
            "<span>" + escapeHTML(f.label) + "</span>" + CLOSE_SVG;
          chip.addEventListener("click", () => clearFilter(f.id, f.value));
          inlineChips.appendChild(chip);
        });
      }
    }
    restoreMobileFilterViewport(viewportSnapshot);
  }

  // =========================================================================
  // DOM injection
  // =========================================================================

  let _injected = false;
  let _mobileSidebarCloseTimer = null;

  /**
   * Animate only explicit drawer interactions. The desktop sidebar already
   * occupies the page when a resize crosses 800px; transitioning that layout
   * into its mobile off-canvas position makes it sweep across the viewport.
   * @param {HTMLElement} sidebar
   * @param {boolean} isOpen
   */
  function setMobileSidebarOpen(sidebar, isOpen) {
    if (_mobileSidebarCloseTimer !== null) {
      window.clearTimeout(_mobileSidebarCloseTimer);
      _mobileSidebarCloseTimer = null;
    }

    sidebar.classList.remove("acf-sidebar--closing");

    if (isOpen) {
      sidebar.classList.add("acf-sidebar--open");
      return;
    }

    if (!sidebar.classList.contains("acf-sidebar--open")) return;

    sidebar.classList.add("acf-sidebar--closing");
    sidebar.classList.remove("acf-sidebar--open");
    _mobileSidebarCloseTimer = window.setTimeout(function () {
      sidebar.classList.remove("acf-sidebar--closing");
      _mobileSidebarCloseTimer = null;
    }, 340);
  }

  function injectFilters() {
    const aisPage = document.querySelector(".ais-page");
    if (!aisPage || _injected) return;
    if (!Object.keys(_state).length) return;

    _injected = true;

    const sidebar = document.createElement("div");
    sidebar.className = "acf-sidebar";
    sidebar.setAttribute("role", "dialog");
    sidebar.setAttribute("aria-modal", "true");
    sidebar.setAttribute(
      "aria-label",
      window.themeStrings?.filters || "Filters",
    );

    // --- Sidebar header (title + close button) ---
    const header = document.createElement("div");
    header.className = "acf-sidebar-header";

    const title = document.createElement("h3");
    title.className = "acf-sidebar-title";
    title.textContent = window.themeStrings?.filters || "Filter";

    const closeBtn = document.createElement("button");
    closeBtn.className = "acf-sidebar-close";
    closeBtn.setAttribute(
      "aria-label",
      window.themeStrings?.closeFilters || "Close filters",
    );
    closeBtn.addEventListener("click", function () {
      setMobileSidebarOpen(sidebar, false);
      document.documentElement.classList.remove("acf-mobile-filter-open");
      document.body.style.overflow = "";
      const toggle = document.querySelector(".acf-mobile-toggle");
      if (toggle) {
        toggle.classList.remove("acf-mobile-toggle--active");
        toggle.setAttribute("aria-expanded", "false");
      }
    });

    // Row 1: "Filter" title + close button
    const titleRow = document.createElement("div");
    titleRow.className = "acf-sidebar-header-row";
    titleRow.appendChild(title);
    titleRow.appendChild(closeBtn);
    header.appendChild(titleRow);

    // Row 2: "Applied Filters (N)" + "CLEAR ALL": hidden until filters active
    const inlineActiveHeader = document.createElement("div");
    inlineActiveHeader.className = "acf-inline-active-header";
    inlineActiveHeader.style.display = "none";
    header.appendChild(inlineActiveHeader);

    // Row 3: chips: hidden until filters active
    const inlineActiveChips = document.createElement("div");
    inlineActiveChips.className = "acf-inline-active-chips";
    inlineActiveChips.style.display = "none";
    header.appendChild(inlineActiveChips);

    sidebar.appendChild(header);

    // --- View items footer (mobile) ---
    const viewItems = document.createElement("div");
    viewItems.className = "acf-view-items";
    const viewBtn = document.createElement("button");
    viewBtn.className = "acf-view-items-btn";
    viewBtn.textContent = window.themeStrings?.viewItems || "View Items";
    viewBtn.addEventListener("click", function () {
      publishViewItemsInteraction();
      setMobileSidebarOpen(sidebar, false);
      document.documentElement.classList.remove("acf-mobile-filter-open");
      document.body.style.overflow = "";
      const toggle = document.querySelector(".acf-mobile-toggle");
      if (toggle) {
        toggle.classList.remove("acf-mobile-toggle--active");
        toggle.setAttribute("aria-expanded", "false");
      }
    });
    viewItems.appendChild(viewBtn);

    // Scrollable content wrapper: keeps view-items button pinned to the bottom
    // (CSS transform on sidebar breaks position:fixed for children)
    const scrollContent = document.createElement("div");
    scrollContent.className = "acf-sidebar-content";

    let _lastToggleGroup = null; // tracks the last toggle accordion for grouping
    // Wrappers that contain at least one exclude or include_filter toggle are always-on
    // and must stay visible regardless of Algolia results. Pure attribute-based include
    // toggles (e.g. Price Drop / Savings) start hidden and are revealed by toggleWrapperShow.
    const alwaysOnToggleWrappers = new Set();
    CONFIG.filters.forEach((cfg) => {
      // Attribute absent from collectionFacets crashes the @app render: see getEnabledFacets() header.
      if (!isFilterEnabledInAlgolia(cfg)) return;
      let wrapper;
      switch (cfg.type) {
        case "range_slider":
          _lastToggleGroup = null;
          wrapper = buildRangeSliderHTML(cfg);
          scrollContent.appendChild(wrapper);
          setupRangeSlider(cfg, wrapper);
          break;
        case "toggle":
          // Group consecutive toggles that share the same label into one accordion
          if (_lastToggleGroup && _lastToggleGroup.dataset.toggleGroup === cfg.label) {
            wrapper = _lastToggleGroup;
          } else {
            wrapper = buildToggleHTML(cfg);
            scrollContent.appendChild(wrapper);
            _lastToggleGroup = wrapper;
          }
          appendToggleItem(cfg, wrapper);
          setupToggle(cfg, wrapper);
          // exclude and include_filter toggles are always-on: mark their wrapper so
          // the post-injection pass below does not hide it.
          if (cfg.mode === "exclude" || cfg.include_filter) {
            alwaysOnToggleWrappers.add(wrapper);
          }
          break;
        case "tag_list":
        case "boolean_list":
        case "refinement_list":
          _lastToggleGroup = null;
          wrapper = buildRefinementListHTML(cfg);
          scrollContent.appendChild(wrapper);
          setupRefinementList(cfg, wrapper);
          break;
      }
    });

    // Post-injection visibility: start data-driven wrappers hidden to prevent a flash
    // of content before the first Algolia response arrives.

    // range_sliders start hidden: refreshFilterDOM reveals them once _boundsReady is true
    // and _hasIsData (set from IS result in onInstantSearchResult) confirms the attribute has data.
    CONFIG.filters.forEach(function (cfg) {
      if (cfg.type !== "range_slider") return;
      const w = scrollContent.querySelector('.acf-filter[data-filter-id="' + cfg.id + '"]');
      if (w) w.style.display = "none";
    });

    // Attribute-based include toggle wrappers start hidden: toggleWrapperShow in
    // onInstantSearchResult reveals them after facets_stats confirms the attribute exists.
    // Wrappers that contain any exclude or include_filter item are always-on and stay visible.
    scrollContent.querySelectorAll(".acf-filter--toggle").forEach(function (w) {
      if (!alwaysOnToggleWrappers.has(w)) {
        w.style.display = "none";
      }
    });

    // Fetch real bounds for any sliders that were matched via title override :
    // fetchInitialBounds ran before _facetOverride was known so it used wrong attrs.
    fetchOverrideBounds();

    sidebar.appendChild(scrollContent);
    sidebar.appendChild(viewItems);

    aisPage.insertBefore(sidebar, aisPage.firstChild);

    // --- Mobile toggle button ---
    const mobileToggle = document.createElement("button");
    mobileToggle.className = "acf-mobile-toggle";
    mobileToggle.setAttribute("aria-expanded", "false");
    mobileToggle.setAttribute(
      "aria-controls",
      sidebar.id ||
        (() => {
          sidebar.id = "acf-sidebar";
          return "acf-sidebar";
        })(),
    );
    mobileToggle.innerHTML =
      '<svg width="17" height="17" viewBox="0 0 17 17" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false">' +
      '<path d="M16.1667 8.0725H5.87083M2.23667 8.0725H0.75M2.23667 8.0725C2.23667 7.59069 2.42807 7.12861 2.76876 6.78792C3.10945 6.44723 3.57152 6.25583 4.05333 6.25583C4.53514 6.25583 4.99722 6.44723 5.33791 6.78792C5.6786 7.12861 5.87 7.59069 5.87 8.0725C5.87 8.55431 5.6786 9.01639 5.33791 9.35708C4.99722 9.69777 4.53514 9.88917 4.05333 9.88917C3.57152 9.88917 3.10945 9.69777 2.76876 9.35708C2.42807 9.01639 2.23667 8.55431 2.23667 8.0725ZM16.1667 13.5783H11.3767M11.3767 13.5783C11.3767 14.0603 11.1848 14.5229 10.844 14.8636C10.5033 15.2044 10.0411 15.3958 9.55917 15.3958C9.07736 15.3958 8.61528 15.2036 8.27459 14.8629C7.9339 14.5222 7.7425 14.0601 7.7425 13.5783M11.3767 13.5783C11.3767 13.0964 11.1848 12.6346 10.844 12.2939C10.5033 11.9531 10.0411 11.7617 9.55917 11.7617C9.07736 11.7617 8.61528 11.9531 8.27459 12.2938C7.9339 12.6344 7.7425 13.0965 7.7425 13.5783M7.7425 13.5783H0.75M16.1667 2.56667H13.5792M9.945 2.56667H0.75M9.945 2.56667C9.945 2.08486 10.1364 1.62278 10.4771 1.28209C10.8178 0.941398 11.2799 0.75 11.7617 0.75C12.0002 0.75 12.2365 0.79699 12.4569 0.888286C12.6773 0.979582 12.8776 1.1134 13.0462 1.28209C13.2149 1.45078 13.3488 1.65105 13.44 1.87146C13.5313 2.09187 13.5783 2.3281 13.5783 2.56667C13.5783 2.80523 13.5313 3.04147 13.44 3.26187C13.3488 3.48228 13.2149 3.68255 13.0462 3.85124C12.8776 4.01994 12.6773 4.15375 12.4569 4.24505C12.2365 4.33634 12.0002 4.38333 11.7617 4.38333C11.2799 4.38333 10.8178 4.19193 10.4771 3.85124C10.1364 3.51055 9.945 3.04848 9.945 2.56667Z" stroke="#2E7631" stroke-width="1.5" stroke-miterlimit="10" stroke-linecap="round"/>' +
      "</svg>" +
      '<span class="acf-mobile-toggle-labels">' +
      '<span class="acf-mobile-toggle-title">' +
      (window.themeStrings?.filters || "Filter") +
      "</span>" +
      "</span>";
    mobileToggle.addEventListener("click", function () {
      const isOpen = mobileToggle.classList.toggle("acf-mobile-toggle--active");
      setMobileSidebarOpen(sidebar, isOpen);
      document.documentElement.classList.toggle("acf-mobile-filter-open", isOpen);
      mobileToggle.setAttribute("aria-expanded", isOpen ? "true" : "false");
      document.body.style.overflow = isOpen ? "hidden" : "";
    });
    const searchHeader = aisPage.querySelector(".ais-search-header");
    if (searchHeader) {
      searchHeader.parentElement.insertBefore(mobileToggle, searchHeader);
    } else {
      aisPage.querySelector(".ais-block")?.prepend(mobileToggle);
    }

    // Active filters bar: placed after search header or before hits
    const sh = aisPage.querySelector(".ais-search-header");
    const barEl = document.createElement("div");
    barEl.className = "acf-active-bar";
    barEl.style.display = "none";
    if (sh) {
      sh.parentElement.insertBefore(barEl, sh.nextSibling);
    } else {
      aisPage.querySelector(".ais-block")?.prepend(barEl);
    }
    renderActiveFiltersBar();
  }

  /**
   * Called by the helper-capture widget on every IS search result.
   * Reads all facet data directly from IS results: no extra requests.
   * - Slider bounds: from facets_stats
   * - Disjunctive lists: from isResult.disjunctiveFacets (IS computes these natively
   *   because we declare disjunctive facets via the virtual widget in init())
   * - Conjunctive lists: from raw facets
   */
  function onInstantSearchResult(isResult) {
    if (!isResult) return;
    const viewportSnapshot = captureMobileFilterViewport();
    _resultsReceived = true;

    const raw = (isResult._rawResults && isResult._rawResults[0]) || isResult;
    const facets = raw.facets || {};
    const facetsStats = raw.facets_stats || {};

    const allergensSt = CONFIG.filters.find(function(f) { return f.attribute === 'meta.product.allergens' && f.exclude_mode; });
    if (allergensSt && _state[allergensSt.id] && _state[allergensSt.id].selected.size > 0) {
      console.log('[ACF exclude] allergens products returned:', raw.nbHits);
    }

    // Build a lookup map of IS native disjunctive facet data.
    // IS always returns ALL values for disjunctive facets (regardless of active selection),
    // so native disjunctive refinement_lists never need refreshExtraCounts.
    const disjFacetsMap = {};
    (isResult.disjunctiveFacets || []).forEach(function (facet) {
      if (facet.name && facet.data) disjFacetsMap[facet.name] = facet.data;
    });


    // Slider bounds from IS facets_stats: no extra request needed.
    // Also set _hasIsData: IS result is the single source of truth for slider visibility.
    CONFIG.filters.forEach(function (cfg) {
      if (cfg.type !== "range_slider") return;
      applyRangeSliderFacetsStats(cfg, facetsStats);
      const st = _state[cfg.id];
      if (st) {
        st._hasIsData = !!(facetsStats[cfg.min_attribute] || facetsStats[cfg._facetOverride || cfg.max_attribute]);
      }
    });

    // Dynamic bounds: re-query Algolia excluding each slider's own filter so the max
    // reflects the true available range (not the IS result which includes the slider's own filter).
    (function () {
      const dynamicIds = [];
      CONFIG.filters.forEach(function (cfg) {
        if (cfg.type !== 'range_slider') return;
        if (!cfg.dynamic_bounds) return;
        const st = _state[cfg.id];
        if (!st || !st._boundsReady) return;
        if (st._lastSelfSearchMs && (Date.now() - st._lastSelfSearchMs) < 1500) return;
        dynamicIds.push(cfg.id);
      });
      if (dynamicIds.length) refreshDynamicSliderBounds(dynamicIds);
    })();

    // Attribute-based toggle visibility: hide wrapper when facets_stats shows no matching data.
    // Toggles using raw filter strings (mode:"exclude" or include_filter) are always visible.
    // Wrappers stay visible if any of their toggle items are active or have matching data.
    const toggleWrapperShow = {}; // label -> boolean (true = show)
    CONFIG.filters.forEach(function (cfg) {
      if (cfg.type !== "toggle") return;
      if (cfg.mode === "exclude" || cfg.include_filter) {
        // visibility_attribute toggles: reflect _visibilityAttrData set by fetchInitialBounds
        if (cfg.visibility_attribute && (cfg.visibility_attribute in _visibilityAttrData)) {
          const st = _state[cfg.id];
          const hasProducts = (raw.nbHits || 0) > 0;
          const isActive = st && st.active;
          if (hasProducts || isActive) {
            toggleWrapperShow[cfg.label] = true;
          } else if (toggleWrapperShow[cfg.label] === undefined) {
            toggleWrapperShow[cfg.label] = false;
          }
        } else {
          // Always-on custom toggles (e.g. Availability): show unless zero products
          const hasProducts = (raw.nbHits || 0) > 0;
          if (hasProducts) {
            toggleWrapperShow[cfg.label] = true;
          } else if (toggleWrapperShow[cfg.label] === undefined) {
            toggleWrapperShow[cfg.label] = false;
          }
        }
        return;
      }
      if (!cfg.attribute) return;
      const st = _state[cfg.id];
      if (!st) return;
      // Once a wrapper is set to show, don't override back to hide
      if (toggleWrapperShow[cfg.label] === true) return;
      // Active toggle -> always keep its wrapper visible
      if (st.active) { toggleWrapperShow[cfg.label] = true; return; }
      const stats = facetsStats[cfg.attribute];
      let hasData = false;
      if (stats) {
        const v = cfg.value || 0;
        if (cfg.operator === ">")       hasData = stats.max > v;
        else if (cfg.operator === ">=") hasData = stats.max >= v;
        else if (cfg.operator === "<")  hasData = stats.min < v;
        else if (cfg.operator === "<=") hasData = stats.min <= v;
        else hasData = true;
      }
      if (hasData) {
        toggleWrapperShow[cfg.label] = true;
      } else if (toggleWrapperShow[cfg.label] === undefined) {
        toggleWrapperShow[cfg.label] = false;
      }
    });
    const _sidebar = document.querySelector(".acf-sidebar");
    if (_sidebar) {
      _sidebar.querySelectorAll("[data-toggle-group]").forEach(function (w) {
        const label = w.dataset.toggleGroup;
        if (label in toggleWrapperShow) {
          w.style.display = toggleWrapperShow[label] ? "" : "none";
        }
      });
    }

    // Show/hide the in-store toggle <li> based on whether in-store products exist.
    // _inStoreHasItems is null until fetchInitialBounds resolves; once set, update visibility.
    // Always keep visible when the toggle is already active (user has turned it on).
    (function () {
      const inStoreCfg = CONFIG.filters.find(function (f) { return f.url_param === 'show_in_store'; });
      if (!inStoreCfg) return;
      if (_inStoreHasItems === null) return; // fetchInitialBounds not yet resolved: leave as-is
      const st = _state[inStoreCfg.id];
      const isActive = st && st.active;
      const shouldShow = _inStoreHasItems || isActive;
      const li = document.querySelector('.acf-item[data-filter-id="' + inStoreCfg.id + '"]');
      if (li) li.style.display = shouldShow ? '' : 'none';
    })();

    // Show/hide the OOS toggle <li> based on whether out-of-stock products exist.
    (function () {
      const oosCfg = CONFIG.filters.find(function (f) { return f.url_param === 'hide_oos'; });
      if (!oosCfg) return;
      if (_oosHasItems === null) return;
      const st = _state[oosCfg.id];
      const isActive = st && st.active;
      const shouldShow = _oosHasItems || isActive;
      const li = document.querySelector('.acf-item[data-filter-id="' + oosCfg.id + '"]');
      if (li) li.style.display = shouldShow ? '' : 'none';
    })();

    // Hide the Availability group wrapper if both toggles are resolved and neither has items to show.
    (function () {
      const inStoreCfg2 = CONFIG.filters.find(function (f) { return f.url_param === 'show_in_store'; });
      const oosCfg2 = CONFIG.filters.find(function (f) { return f.url_param === 'hide_oos'; });
      if (!inStoreCfg2 && !oosCfg2) return; // no availability toggles configured
      if (_inStoreHasItems === null || _oosHasItems === null) return; // still loading
      const inStoreActive = inStoreCfg2 && _state[inStoreCfg2.id] && _state[inStoreCfg2.id].active;
      const oosActive = oosCfg2 && _state[oosCfg2.id] && _state[oosCfg2.id].active;
      const inStoreVisible = inStoreCfg2 && (_inStoreHasItems || inStoreActive);
      const oosVisible = oosCfg2 && (_oosHasItems || oosActive);
      if (!inStoreVisible && !oosVisible) {
        const wrapperLabel = (inStoreCfg2 || oosCfg2).label;
        const wrapper = document.querySelector('[data-toggle-group="' + wrapperLabel + '"]');
        if (wrapper) wrapper.style.display = 'none';
      }
    })();

    // Refinement lists: native disjunctive filters read from IS disjunctiveFacets (always complete).
    // For non-native disjunctive lists WITH active selections, IS returns filtered counts :
    // collect them for refreshExtraCounts batch instead.
    const selectedDisjunctiveIds = [];
    CONFIG.filters.forEach(function (cfg) {
      if (
        cfg.type !== "refinement_list" &&
        cfg.type !== "boolean_list" &&
        cfg.type !== "tag_list"
      )
        return;
      const st = _state[cfg.id];
      if (isNativeDisjunctive(cfg)) {
        const hasQuotedSelection = st && [...st.selected].some(_needsQuoting);
        if (hasQuotedSelection) {
          // Quoted values are carried in the filters string, so IS cannot issue its
          // normal self-excluding disjunctive facet query. Fetch the complete option
          // counts separately to keep the rest of the list available.
          selectedDisjunctiveIds.push(cfg.id);
        } else {
          // IS natively handles these: data is always complete, no extra request needed
          applyRefinementListFacets(cfg, {
            [cfg.attribute]: disjFacetsMap[cfg.attribute] || {},
          });
        }
      } else if (cfg.type === 'boolean_list' && cfg._parsedBoolLabels) {
        // Active boolean options are applied as one filters-string group. Re-fetch
        // this list without its own group so unchecked OR choices keep complete counts.
        if (st && st.selected.size > 0) {
          selectedDisjunctiveIds.push(cfg.id);
        } else {
          const boolFacets = {};
          Object.keys(cfg._parsedBoolLabels).forEach(function (attr) {
            if (isBoolAttrEnabled(attr)) {
              boolFacets[attr] = disjFacetsMap[attr] || facets[attr] || {};
            }
          });
          applyRefinementListFacets(cfg, boolFacets);
        }
      } else if (cfg.operator_type === "disjunctive") {
        selectedDisjunctiveIds.push(cfg.id);
      } else {
        applyRefinementListFacets(cfg, facets);
      }
    });

    refreshFilterDOM();
    restoreMobileFilterViewport(viewportSnapshot);

    // Extra batch: one request for all disjunctive facets with active selections
    if (selectedDisjunctiveIds.length) {
      refreshExtraCounts(selectedDisjunctiveIds);
    } else {
      refreshExcludeModeOptionCounts();
    }

    // Hit counts for attribute-based toggle filters (e.g. Savings / compare_at_price > 0)
    refreshToggleCounts();

    // [ACF] Filter visibility comparison: IS result vs storefront config
    (function _logFilterVisibility() {
      const rows = {};
      const seenToggleGroups = new Set();

      CONFIG.filters.forEach(function (cfg) {
        const st = _state[cfg.id];
        let expectedShow = true;
        let reason = '';

        if (cfg.type === 'range_slider') {
          expectedShow = !!(st && st._hasIsData);
          const minStat = facetsStats[cfg.min_attribute];
          const maxStat = facetsStats[cfg._facetOverride || cfg.max_attribute];
          reason = (minStat || maxStat) ? 'facets_stats has data' : 'not in facets_stats';
        } else if (cfg.type === 'toggle') {
          if (seenToggleGroups.has(cfg.label)) return;
          seenToggleGroups.add(cfg.label);
          expectedShow = toggleWrapperShow[cfg.label] !== false;
          if (cfg.mode === 'exclude' || cfg.include_filter) {
            reason = 'nbHits=' + (raw.nbHits || 0);
          } else {
            const facetCounts = facets[cfg.attribute] || disjFacetsMap[cfg.attribute];
            if (facetCounts) {
              const entries = Object.keys(facetCounts).slice(0, 6).map(function(k) { return k + ':' + facetCounts[k]; }).join(', ');
              reason = (cfg.attribute || '') + ' -> ' + entries + (Object.keys(facetCounts).length > 6 ? '…' : '');
            } else {
              reason = 'not in facets (' + (cfg.attribute || '') + ')';
            }
          }
        } else {
          const w = document.querySelector('.acf-filter[data-filter-id="' + cfg.id + '"]');
          const itemCount = w ? w.querySelectorAll('.acf-item:not(.acf-item--skeleton)').length : 0;
          expectedShow = itemCount > 0;
          if (cfg.type === 'boolean_list' && cfg._parsedBoolLabels) {
            const parts = Object.keys(cfg._parsedBoolLabels).map(function(attr) {
              const attrFacets = disjFacetsMap[attr] || facets[attr] || {};
              return cfg._parsedBoolLabels[attr] + ': true=' + (attrFacets['true'] || 0);
            });
            reason = parts.join(' | ');
          } else if (cfg.attribute) {
            const facetCounts = disjFacetsMap[cfg.attribute] || facets[cfg.attribute];
            if (facetCounts) {
              const entries = Object.keys(facetCounts).slice(0, 6).map(function(k) { return k + ':' + facetCounts[k]; }).join(', ');
              reason = cfg.attribute + ' -> ' + entries + (Object.keys(facetCounts).length > 6 ? '…' : '');
            } else {
              reason = 'not in facets (' + cfg.attribute + ')';
            }
          } else {
            reason = itemCount + ' items rendered';
          }
        }

        const el = cfg.type === 'toggle'
          ? document.querySelector('[data-toggle-group="' + cfg.label + '"]')
          : document.querySelector('.acf-filter[data-filter-id="' + cfg.id + '"]');
        const actualShow = el ? el.style.display !== 'none' : null;
        rows[cfg.label || cfg.id] = {
          type: cfg.type,
          expected: expectedShow ? 'SHOW' : 'HIDE',
          actual: actualShow === null ? '?' : (actualShow ? 'SHOW' : 'HIDE'),
          reason: reason,
        };
      });

      console.group('[ACF] Filter visibility comparison (nbHits=' + (raw.nbHits || 0) + ')');
      console.table(rows, ['type', 'expected', 'actual', 'reason']);
      console.groupEnd();
    })();
  }

  /** Refresh DOM for all filter widgets without moving the active mobile control. */
  function refreshFilterDOM() {
    const viewportSnapshot = captureMobileFilterViewport();
    CONFIG.filters.forEach(function (cfg) {
      const st = _state[cfg.id];
      if (!st) return;
      if (cfg.type === "range_slider") {
        const w = document.querySelector('.acf-filter[data-filter-id="' + cfg.id + '"]');
        if (w) {
          const shouldShow = st._hasIsData === true;
          w.style.display = shouldShow ? "" : "none";
          if (shouldShow && st._boundsReady && st.updateDOM) st.updateDOM();
        }
        return;
      }
      if (
        cfg.type === "refinement_list" ||
        cfg.type === "boolean_list" ||
        cfg.type === "tag_list"
      ) {
        const w = document.querySelector(
          '.acf-filter[data-filter-id="' + cfg.id + '"]',
        );
        if (w) renderRefinementListItems(cfg, w);
      }
    });
    _refreshVisibilityAttrGroups();

    // Update in-store toggle <li> visibility once fetchInitialBounds data is available
    const inStoreCfgRefresh = CONFIG.filters.find(function (f) { return f.url_param === 'show_in_store'; });
    if (inStoreCfgRefresh && _inStoreHasItems !== null) {
      const st = _state[inStoreCfgRefresh.id];
      const isActive = st && st.active;
      const shouldShow = _inStoreHasItems || isActive;
      const li = document.querySelector('.acf-item[data-filter-id="' + inStoreCfgRefresh.id + '"]');
      if (li) li.style.display = shouldShow ? '' : 'none';
    }
    // Update OOS toggle <li> visibility once fetchInitialBounds data is available
    const oosCfgRefresh = CONFIG.filters.find(function (f) { return f.url_param === 'hide_oos'; });
    if (oosCfgRefresh && _oosHasItems !== null) {
      const st = _state[oosCfgRefresh.id];
      const isActive = st && st.active;
      const shouldShow = _oosHasItems || isActive;
      const li = document.querySelector('.acf-item[data-filter-id="' + oosCfgRefresh.id + '"]');
      if (li) li.style.display = shouldShow ? '' : 'none';
    }
    // Hide Availability wrapper if both toggles have no items to show
    (function () {
      const inStoreCfgW = CONFIG.filters.find(function (f) { return f.url_param === 'show_in_store'; });
      const oosCfgW = CONFIG.filters.find(function (f) { return f.url_param === 'hide_oos'; });
      if (!inStoreCfgW && !oosCfgW) return;
      if (_inStoreHasItems === null || _oosHasItems === null) return;
      const inStoreActiveW = inStoreCfgW && _state[inStoreCfgW.id] && _state[inStoreCfgW.id].active;
      const oosActiveW = oosCfgW && _state[oosCfgW.id] && _state[oosCfgW.id].active;
      const inStoreVisibleW = inStoreCfgW && (_inStoreHasItems || inStoreActiveW);
      const oosVisibleW = oosCfgW && (_oosHasItems || oosActiveW);
      if (!inStoreVisibleW && !oosVisibleW) {
        const wrapperLabelW = (inStoreCfgW || oosCfgW).label;
        const wrapperEl = document.querySelector('[data-toggle-group="' + wrapperLabelW + '"]');
        if (wrapperEl) wrapperEl.style.display = 'none';
      }
    })();
    restoreMobileFilterViewport(viewportSnapshot);
  }

  /**
   * Hides toggle-group accordions whose visibility_attribute returned no data in fetchInitialBounds.
   * Called from refreshFilterDOM so groups are hidden after the initial bounds fetch resolves.
   */
  function _refreshVisibilityAttrGroups() {
    if (!Object.keys(_visibilityAttrData).length) return;
    const sidebar = document.querySelector(".acf-sidebar");
    if (!sidebar) return;
    sidebar.querySelectorAll("[data-toggle-group]").forEach(function (w) {
      let shouldHide = false;
      w.querySelectorAll(".acf-item[data-filter-id]").forEach(function (li) {
        const st = _state[li.dataset.filterId];
        if (!st) return;
        const attr = st.config.visibility_attribute;
        if (attr && (attr in _visibilityAttrData) && !_visibilityAttrData[attr] && !st.active) {
          shouldHide = true;
        }
      });
      if (shouldHide) w.style.display = "none";
    });
  }

  // =========================================================================
  // collectionFacets guard
  // =========================================================================

  // Lazily-built Set of enabled attribute names.
  // Collection pages: built from algoliaShopify.collectionFacets (collection-specific).
  // Search pages: built from algoliaShopify.facets (global facet config, same source IS uses).
  // null = not yet built; populated on first call to getEnabledFacets() after IS starts.
  let _enabledFacets = null;

  /**
   * Returns the Set of attribute names that are configured as enabled facets for the
   * current collection/locale. Builds the Set on first call once IS has populated
   * algoliaShopify.collectionFacets (collection pages) or algoliaShopify.facets (search).
   * Returns null if neither source is available yet (e.g. on initial paint).
   *
   * WHY THIS GATE EXISTS: crash prevention:
   * The @app bundle (algolia_instant_search.js) can only handle refinements for attributes
   * that appear in collectionFacets. Refining on an absent attribute causes:
   *   "Cannot read properties of undefined (reading 'title')" inside algolia_instant_search.js
   * The render pipeline aborts, the product grid never updates, and the URL param is never
   * written: so it presents as "filter does nothing" rather than a visible error.
   *
   * Real-world incident: on French (Hrz-dev_products_canada_fr), meta.terpene.terpenes,
   * meta.product.ontario_grown, and meta.product.craft are absent from collectionFacets,
   * so any refinement on them crashed the @app render. On English they are enabled facets
   * so the same filters worked. A narrower fix that gated only boolean_list left the
   * refinement_list (terpenes) crash unfixed: the gate must cover ALL filter types.
   *
   * KEY INVARIANT: fallback-allow when null:
   * All helpers (isAttrEnabled, isFilterEnabledInAlgolia) return true / allow the attribute
   * when getEnabledFacets() returns null. This preserves behavior on initial paint and must
   * NOT be changed to default-deny, or filters would flicker/disappear on first load.
   *
   * To make a hidden filter work: enable the attribute as a facet in the Search & Discovery
   * @app config for that collection/locale. That is a config change, not a theme change.
   */
  function getEnabledFacets() {
    if (_enabledFacets) return _enabledFacets;
    const as = window.algoliaShopify;
    const cf = as && (as.collectionFacets || as.facets);
    if (!Array.isArray(cf) || !cf.length) return null;
    _enabledFacets = new Set();
    cf.forEach(function (f) {
      if (f.enabled && f.name) _enabledFacets.add(f.name);
    });
    return _enabledFacets;
  }

  /**
   * Returns false if NONE of the filter's attributes are in collectionFacets.
   * For range_slider: if neither configured attribute matches, falls back to
   * finding a collectionFacets slider entry whose title matches the block label.
   * When a title-match override is set, the slider renders with min=0 and uses
   * the matched attribute name as the effective max_attribute.
   * Skips the check (returns true) if collectionFacets isn't available yet.
   */
  function isFilterEnabledInAlgolia(cfg) {
    const enabledFacets = getEnabledFacets();
    if (!enabledFacets) return true; // collectionFacets not ready: allow all
    if (cfg.type === "range_slider") {
      // resolveSliderOverrides() may have already set _facetOverride: check first
      if (cfg._facetOverride) return true;
      const attrs = [];
      if (cfg.min_attribute) attrs.push(cfg.min_attribute);
      if (cfg.max_attribute) attrs.push(cfg.max_attribute);
      if (
        attrs.some(function (a) {
          return enabledFacets.has(a);
        })
      )
        return true;
      // Neither configured attribute matches: try title-match via resolveSliderOverrides
      resolveSliderOverrides();
      if (cfg._facetOverride) return true;
      // console.info(
      //   '[acf] Filter "' +
      //     cfg.label +
      //     '" skipped: [' +
      //     attrs.join(", ") +
      //     "] not in collectionFacets.",
      // );
      return false;
    }
    let attrs = [];
    if (cfg.type === "toggle") {
      // Toggles that use a raw filter string (exclude_filter or include_filter) don't
      // reference a facet attribute: always allow them.
      if (cfg.mode === "exclude") return true;
      if (cfg.include_filter) return true;
      if (cfg.attribute) attrs.push(cfg.attribute);
    } else {
      attrs = getAttrs(cfg);
    }
    if (!attrs.length) return false;
    const isEnabled = attrs.some(function (a) {
      return enabledFacets.has(a);
    });
    // if (!isEnabled) {
    //   console.info(
    //     '[acf] Filter "' +
    //       cfg.label +
    //       '" skipped: [' +
    //       attrs.join(", ") +
    //       "] not in collectionFacets.",
    //   );
    // }
    return isEnabled;
  }

  // True when an attribute is an enabled facet for this collection/locale.
  // Falls back to true when collectionFacets hasn't loaded yet (no regression).
  function isAttrEnabled(attr) {
    var enabled = getEnabledFacets();
    if (!enabled) return true;
    return enabled.has(attr);
  }
  // Alias so all existing isBoolAttrEnabled(...) call sites continue to work unchanged.
  var isBoolAttrEnabled = isAttrEnabled;

  /**
   * Resolves _facetOverride for any range_slider whose configured attributes aren't in
   * collectionFacets. Called eagerly from _acfGetDisjunctiveSearchParams (before each IS
   * search) so the override is set before URL seeding and getWidgetUiState run.
   */
  function resolveSliderOverrides() {
    const enabledFacets = getEnabledFacets();
    if (!enabledFacets) return; // facets not ready yet
    const as = window.algoliaShopify;
    const cf = as && (as.collectionFacets || as.facets);
    CONFIG.filters.forEach(function (cfg) {
      if (cfg.type !== "range_slider" || cfg._facetOverride) return;
      const attrs = [];
      if (cfg.min_attribute) attrs.push(cfg.min_attribute);
      if (cfg.max_attribute) attrs.push(cfg.max_attribute);
      if (
        attrs.some(function (a) {
          return enabledFacets.has(a);
        })
      )
        return; // configured attrs work
      if (!Array.isArray(cf) || !cfg.label) return;
      const match = cf.find(function (f) {
        return (
          f.enabled &&
          f.type === "slider" &&
          f.title &&
          f.title.toLowerCase() === cfg.label.toLowerCase()
        );
      });
      if (match) {
        cfg._facetOverride = match.name;
        const st = _state[cfg.id];
        if (st) {
          st._boundsReady = false;
          st._boundsLoading = false;
        }
      }
    });
  }

  /** Returns the effective max attribute: uses _facetOverride when a title-match was found. */
  function getEffectiveMaxAttr(cfg) {
    return cfg._facetOverride || cfg.max_attribute;
  }

  /**
   * Returns false if min_attribute is absent from collectionFacets (or a _facetOverride
   * is active, meaning we matched by title and only have a max attribute).
   * When false, the slider renders with min locked at 0 and no min filter applied.
   */
  function isMinAttributeEnabled(cfg) {
    if (cfg._facetOverride) return false; // title-match override: min always 0
    if (!cfg.min_attribute) return false;
    const enabledFacets = getEnabledFacets();
    if (!enabledFacets) return true; // collectionFacets not ready: assume enabled
    return enabledFacets.has(cfg.min_attribute);
  }

  // True when a PDP->PLP scroll-restore is pending for the current URL.
  // In that case the back/forward viewport is owned by restorePlpScrollPosition()
  // in algolia-plp-hooks.js, so the filter-back handler must NOT scroll to top.
  function _plpScrollRestorePending() {
    try {
      var raw = sessionStorage.getItem('ocs:plp-scroll-restore');
      if (!raw) return false;
      var st = JSON.parse(raw);
      if (!st || st.url !== window.location.href) return false;
      // 30-min TTL mirrors PLP_SCROLL_RESTORE_TTL_MS; expired => restore won't fire => allow scroll.
      if (Date.now() - st.timestamp > 30 * 60 * 1000) return false;
      return true;
    } catch (_) { return false; }
  }

  // =========================================================================
  // Bootstrap
  // =========================================================================

  function init() {
    if (_initialized) return;
    _initialized = true;
    // Initialize state for all filters: collectionFacets isn't available yet at this point
    // (IS hasn't started). The collectionFacets gate is applied in injectFilters() and
    // _acfGetDisjunctiveSearchParams() where IS has already populated collectionFacets.
    CONFIG.filters.forEach((cfg) => {
      switch (cfg.type) {
        case "range_slider":
          initRangeSliderState(cfg);
          break;
        case "toggle":
          initToggleState(cfg);
          break;
        case "tag_list":
        case "boolean_list":
        case "refinement_list":
          initRefinementListState(cfg);
          break;
      }
    });

    // Sync toggle state immediately so window._acfActiveToggles reflects
    // any toggles pre-seeded from URL params on page load.
    _syncToggleState();

    // Write active toggle URL params immediately after state init so that
    // default_active toggles (e.g. show_in_store) have their URL params set
    // before IS fires its first history.replaceState. Without this, IS's
    // initial router write happens before _installToggleUrlInterceptor patches
    // history, meaning the very first URL write strips the toggle param and
    // seeded=false on any subsequent reload: causing the checkbox to appear
    // unchecked despite default_active being true.
    _writeToggleUrlParams();

    // Fetch slider bounds once from the unfiltered collection: locks min/max permanently
    fetchInitialBounds();

    // Install history interceptor so toggle URL params survive IS's debounced router writes.
    _installToggleUrlInterceptor();

    // Sync toggle state when the user navigates Back/Forward. Without this,
    // _state stays stale after popstate, causing IS to re-search with the
    // removed filter and the interceptor to re-pollute the URL.
    window.addEventListener('popstate', function () {
      _inPopstate = true;
      const _restoreActive = _plpScrollRestorePending();

      // Re-derive each toggle's active state from the now-reverted URL
      const urlParams = new URLSearchParams(window.location.search);
      CONFIG.filters.forEach(function (cfg) {
        if (cfg.type !== 'toggle') return;
        const st = _state[cfg.id];
        if (!st) return;
        const urlKey = cfg.url_param || cfg.attribute;
        if (!urlKey) return;
        const nowActive =
          urlParams.get(urlKey) === 'true' || urlParams.get(urlKey) === '1';
        st.active = nowActive;

        // Sync the sidebar checkbox and selected class
        const li = document.querySelector(
          '.acf-item[data-filter-id="' + cfg.id + '"]'
        );
        if (li) {
          const cb = li.querySelector('input[type="checkbox"]');
          if (cb) cb.checked = nowActive;
          li.classList.toggle('acf-item--selected', nowActive);
        }
      });

      // Redraw the active-filters bar and notify external toggle consumers
      renderActiveFiltersBar();
      _syncToggleState();

      // IS's own popstate handler already ran (it registered first) with the
      // now-stale _state. Kick a fresh search with the corrected _state, then
      // re-enable the interceptor and write the correct toggle params.
      setTimeout(function () {
        const helper = window._algoliaHelper;
        const is = window._algoliaIS;
        const urlParams = new URLSearchParams(window.location.search);

        const scrollTopUnlessRestore = function () {
          if (_restoreActive) return; // PDP->PLP bfcache return: leave the viewport to restorePlpScrollPosition()
          const scrollToPageTop = function () { window.scrollTo({ top: 0, behavior: "smooth" }); };
          if (window.matchMedia && window.matchMedia("(min-width: 800px)").matches && window.requestAnimationFrame) {
            window.requestAnimationFrame(scrollToPageTop);
          } else {
            scrollToPageTop();
          }
        };

        // Sync ACF _state for all filter types from the reverted URL
        CONFIG.filters.forEach(function (cfg) {
          const st = _state[cfg.id];
          if (!st) return;

          if (cfg.type === 'refinement_list' || cfg.type === 'tag_list' || cfg.type === 'boolean_list') {
            if (isNativeDisjunctive(cfg)) {
              const vals = [];
              urlParams.forEach(function (val, key) {
                const match = key.match(/^refinementList\[(.+)\]\[\d+\]$/);
                if (match && match[1] === cfg.attribute) vals.push(val);
              });
              st.selected.clear();
              vals.forEach(function (v) { st.selected.add(v); });
            } else if (cfg.type === 'boolean_list' && cfg._parsedBoolLabels) {
              // boolean_list writes to IS refinementList[attr]: no url_param, so
              // read back from the reverted URL's refinementList[attr][N] params.
              // Each attr key in _parsedBoolLabels is the Algolia attribute; it is
              // present in the URL iff the checkbox was checked before Back was pressed.
              st.selected.clear();
              Object.keys(cfg._parsedBoolLabels).forEach(function (attr) {
                if (!isBoolAttrEnabled(attr)) return;
                urlParams.forEach(function (val, key) {
                  const match = key.match(/^refinementList\[(.+)\]\[\d+\]$/);
                  if (match && match[1] === attr) st.selected.add(attr);
                });
              });
            } else if (cfg.url_param) {
              const raw = urlParams.get(cfg.url_param);
              st.selected.clear();
              if (raw) {
                String(raw).split(',').forEach(function (v) {
                  const t = decodeURIComponent(v.trim());
                  if (t) st.selected.add(t);
                });
              }
            }
          }

          if (cfg.type === 'range_slider') {
            const maxAttr = getEffectiveMaxAttr(cfg);
            if (!maxAttr) return;
            const raw = urlParams.get('range[' + maxAttr + ']');
            if (raw) {
              const parts = raw.split(':');
              if (parts.length === 2) {
                const newMin = parseFloat(parts[0]);
                const newMax = parseFloat(parts[1]);
                if (!isNaN(newMin)) st.currentMin = newMin;
                if (!isNaN(newMax)) st.currentMax = newMax;
                st.userInteracted = true;
              }
            } else {
              st.currentMin = st.dataMin;
              st.currentMax = st.dataMax;
              st.userInteracted = false;
            }
          }
        });

        // Redraw active bar and sidebar with correct ACF state
        renderActiveFiltersBar();
        _syncToggleState();
        refreshFilterDOM();

        // Force IS to re-search with the correct URL state by calling setUiState.
        // is.helper.search() re-fires with IS's stale internal state: setUiState
        // updates IS's own uiState from the URL then triggers a clean search.
        if (is) {
          const currentUiState = is.getUiState();
          const indexName = Object.keys(currentUiState)[0];
          if (indexName) {
            const indexState = currentUiState[indexName];

            // Build correct refinementList from URL
            const refinementList = {};
            urlParams.forEach(function (val, key) {
              const match = key.match(/^refinementList\[(.+)\]\[\d+\]$/);
              if (match) {
                if (!refinementList[match[1]]) refinementList[match[1]] = [];
                refinementList[match[1]].push(val);
              }
            });

            // Build correct range from URL
            const range = {};
            urlParams.forEach(function (val, key) {
              const match = key.match(/^range\[(.+)\]$/);
              if (match) range[match[1]] = val;
            });

            const newIndexState = Object.assign({}, indexState, { refinementList });
            if (Object.keys(range).length) {
              newIndexState.range = range;
            } else {
              delete newIndexState.range;
            }

            // Sync toggle url_params in IS uiState from the already-synced _state
            // so stale values from indexState (e.g. price_drop: 'true') don't persist.
            CONFIG.filters.forEach(function (cfg) {
              if (cfg.type !== 'toggle') return;
              const urlKey = cfg.url_param || cfg.attribute;
              if (!urlKey) return;
              const st = _state[cfg.id];
              if (!st) return;
              if (st.active) {
                newIndexState[urlKey] = 'true';
              } else {
                delete newIndexState[urlKey];
              }
            });

            _inPopstate = false;
            is.setUiState(Object.assign({}, currentUiState, { [indexName]: newIndexState }));
            _writeToggleUrlParams();
            scrollTopUnlessRestore();
            return;
          }
        }

        // Fallback: no IS instance
        if (helper) helper.search();
        _inPopstate = false;
        _writeToggleUrlParams();
        scrollTopUnlessRestore();
      }, 0);
    });

    if (window.algoliaShopify?.hooks?.registerHook) {
      // NOTE: buildFilterString() does NOT include getCollectionFilter(). The collection
      // filter is only used in buildFilterStringExcluding() (standalone search calls) and
      // in onInstantSearchResult(). It is NOT injected into the IS filter string here.
      // Filter string hook: injects slider/toggle/conjunctive filter conditions
      algoliaShopify.hooks.registerHook(
        "beforeInstantSearchFiltersString",
        function (filterString) {
          const custom = buildFilterString();
          if (!custom) return filterString;
          const result = filterString ? filterString + " AND " + custom : custom;
          return result;
        },
      );
    }

    // Called by the helper-capture widget's render() in algolia-custom-hooks.js
    // on every IS search result: no race condition, no polling needed.
    window._acfOnSearchResult = function (results) {
      onInstantSearchResult(results);
    };

    // Called from helper-capture widget's getWidgetSearchParameters in algolia-custom-hooks.js.
    // Injects native disjunctive refinement_list selections into IS search parameters directly,
    // so they go through IS's facetFilters (not the filters string) and merge correctly with
    // the native Algolia IS panel's own disjunctive facet selections (OR, not AND).
    // uiState is passed from IS routing: contains refinementList[attr] from the current URL.
    window._acfGetDisjunctiveSearchParams = function (
      searchParameters,
      uiState,
    ) {
      // Resolve overrides early: collectionFacets (collection pages) or facets (search page)
      // is populated by IS before this is called, so _facetOverride will be set here
      // before URL seeding and getWidgetUiState run.
      resolveSliderOverrides();

      let params = searchParameters;
      const routeRefinements = (uiState && uiState.refinementList) || {};
      const routeRanges = (uiState && uiState.range) || {};

      // Seed slider state from URL range params on initial load
      if (!_resultsReceived) {
        CONFIG.filters.forEach(function (cfg) {
          if (cfg.type !== "range_slider") return;
          const st = _state[cfg.id];
          if (!st || st.userInteracted) return;
          const maxAttr = getEffectiveMaxAttr(cfg);
          const rangeVal = routeRanges[maxAttr];
          if (rangeVal && rangeVal.includes(":")) {
            const parts = rangeVal.split(":");
            const rMin = Number(parts[0]);
            const rMax = Number(parts[1]);
            if (!isNaN(rMin) && !isNaN(rMax)) {
              st.currentMin = rMin;
              st.currentMax = rMax;
              st.userInteracted = true;
            }
          }
        });

        // Seed toggle from uiState on back/forward navigation.
        if (uiState) {
          CONFIG.filters.forEach(function (cfg) {
            if (cfg.type !== 'toggle') return;
            const urlKey = cfg.url_param || cfg.attribute;
            if (!urlKey) return;
            const st = _state[cfg.id];
            if (!st) return;
            st.active = uiState[urlKey] === 'true';
          });
        }

        // Seed non-native filters (conjunctive refinement_list)
        // from uiState on initial load / back-forward navigation.
        CONFIG.filters.forEach(function (cfg) {
          if (isNativeDisjunctive(cfg)) return;
          if (
            cfg.type !== "refinement_list" &&
            cfg.type !== "tag_list" &&
            cfg.type !== "boolean_list"
          )
            return;
          if (!cfg.url_param || !uiState) return;
          const st = _state[cfg.id];
          if (!st) return;
          const raw = uiState[cfg.url_param];
          if (!raw) return;
          // Clear and re-seed so back/forward navigation always reflects the URL
          st.selected.clear();
          String(raw)
            .split(",")
            .forEach(function (v) {
              const t = decodeURIComponent(v.trim());
              if (t) st.selected.add(t);
            });
          st._prevSelected = new Set(st.selected);
        });
      }

      CONFIG.filters.forEach(function (cfg) {
        if (!isNativeDisjunctive(cfg)) return;
        // Attribute absent from collectionFacets crashes the @app render: see getEnabledFacets() header.
        if (!isAttrEnabled(cfg.attribute)) return;
        // Always declare the attribute as a disjunctive facet so IS requests its data
        if (!params.isDisjunctiveFacet(cfg.attribute)) {
          params = params.addDisjunctiveFacet(cfg.attribute);
        }
        const st = _state[cfg.id];
        if (!st) return;
        // On initial page load (before first results), seed _state.selected from IS routing so
        // UI checkboxes reflect URL-loaded refinements (e.g. back/forward navigation).
        const routeVals = routeRefinements[cfg.attribute] || [];
        if (!_resultsReceived && routeVals.length && !st.selected.size) {
          routeVals.forEach(function (val) {
            if (!val) return;
            // Only seed values that belong to this filter: same logic as applyRefinementListFacets
            var vmap = cfg._parsedValueMap;
            if (vmap) { if (!vmap.hasOwnProperty(val)) return; }
            else if (cfg.prefix && !val.startsWith(cfg.prefix)) return;
            st.selected.add(val);
          });
          st._prevSelected = new Set(st.selected);
        }
        const useQuotedFilterGroup = [...st.selected].some(_needsQuoting);
        st.selected.forEach(function (val) {
          // If one value requires quoting, buildFilterString routes the complete OR
          // selection through the filters string. Do not split the selection across
          // filters and facetFilters, which Algolia would combine with AND.
          if (useQuotedFilterGroup) return;
          params = params.addDisjunctiveFacetRefinement(cfg.attribute, val);
        });
      });

      // Declare exclude-mode refinement_list attributes as regular facets so IS fetches
      // their value lists for the checkbox UI.
      // Declare as disjunctive facet so IS returns ALL allergen values in its sub-query
      // regardless of which ones are excluded via NOT filters. This prevents applyRefinementListFacets
      // from clearing st.selected values that don't appear in a post-filter facet response.
      // Step-6b (which caused 0-results by writing to IS refinementList) is removed: this is safe.
      CONFIG.filters.forEach(function (cfg) {
        if (!cfg.exclude_mode) return;
        if (cfg.type !== 'refinement_list' && cfg.type !== 'tag_list') return;
        if (!params.isDisjunctiveFacet(cfg.attribute)) {
          params = params.addDisjunctiveFacet(cfg.attribute);
        }
        if (!_resultsReceived) {
          const st = _state[cfg.id];
          if (!st) return;
          const routeVals = routeRefinements[cfg.attribute] || [];
          if (routeVals.length && !st.selected.size) {
            routeVals.forEach(function (val) {
              if (!val) return;
              var vmap = cfg._parsedValueMap;
              if (vmap) { if (!vmap.hasOwnProperty(val)) return; }
              else if (cfg.prefix && !val.startsWith(cfg.prefix)) return;
              st.selected.add(val);
            });
            st._prevSelected = new Set(st.selected);
          }
        }
        // Do NOT call addDisjunctiveFacetRefinement: the NOT is applied via buildFilterString
      });

      // Boolean-list options map to separate attributes. Declare each attribute as
      // a disjunctive facet for counts and routing, but do not add facet refinements:
      // separate facetFilters would be ANDed by Algolia. buildFilterString() applies
      // the selected attributes together using the configured OR/AND operator.
      CONFIG.filters.forEach(function (cfg) {
        if (cfg.type !== 'boolean_list' || !cfg._parsedBoolLabels) return;
        const st = _state[cfg.id];
        if (!st) return;
        Object.keys(cfg._parsedBoolLabels).forEach(function (attr) {
          if (!isBoolAttrEnabled(attr)) return;
          if (!params.isDisjunctiveFacet(attr)) {
            params = params.addDisjunctiveFacet(attr);
          }
          // uiState.refinementList keeps these values in the URL and can feed them
          // back into SearchParameters before this virtual widget runs. Remove that
          // per-attribute refinement so it cannot AND with the grouped filters clause.
          if (params.isDisjunctiveFacetRefined(attr)) {
            params = params.removeDisjunctiveFacetRefinement(attr);
          }
          if (!_resultsReceived) {
            const routeVals = routeRefinements[attr] || [];
            if (routeVals.includes('true') && !st.selected.has(attr)) {
              st.selected.add(attr);
              st._prevSelected = new Set(st.selected);
            }
          }
        });
      });

      // Register range_slider numeric attributes as IS facets so facets_stats is
      // populated in the main IS result: used to gate slider visibility.
      CONFIG.filters.forEach(function (cfg) {
        if (cfg.type !== 'range_slider') return;
        [cfg.min_attribute, cfg.max_attribute].forEach(function (attr) {
          if (!attr) return;
          if (params.facets.indexOf(attr) === -1) {
            params = params.addFacet(attr);
          }
        });
      });

      // Register attribute-based include toggle attributes as IS facets so facets_stats
      // is populated: used by toggleWrapperShow to gate visibility (e.g. compare_at_price).
      CONFIG.filters.forEach(function (cfg) {
        if (cfg.type !== 'toggle') return;
        if (cfg.mode === 'exclude' || cfg.include_filter) return;
        if (!cfg.attribute) return;
        if (params.facets.indexOf(cfg.attribute) === -1) {
          params = params.addFacet(cfg.attribute);
        }
      });

      return params;
    };

    // Called from helper-capture widget's getWidgetUiState in algolia-custom-hooks.js.
    // Only handles our custom non-native URL params (range, toggle, legacy url_param keys).
    // Native disjunctive refinementList entries are managed entirely by IS: we never
    // touch uiState.refinementList here to avoid clobbering IS's own facet panel widgets.
    window._acfGetWidgetUiState = function (uiState, searchParameters) {
      let newUiState = uiState;

      // Range sliders
      const range = Object.assign({}, uiState.range);
      let rangeChanged = false;
      CONFIG.filters.forEach(function (cfg) {
        if (cfg.type !== "range_slider") return;
        // No isFilterEnabledInAlgolia gate: range sliders are self-managing via _hasIsData
        // (set in onInstantSearchResult). Removing the gate ensures THC/CBD sliders write their active range to URL state.
        const st = _state[cfg.id];
        if (!st) return;
        const maxAttr = getEffectiveMaxAttr(cfg);
        if (!maxAttr) return;
        const atMin = st.currentMin <= st.dataMin;
        const atMax = st.currentMax >= st.dataMax;
        if (!atMin || !atMax) {
          range[maxAttr] = st.currentMin + ":" + st.currentMax;
          rangeChanged = true;
        } else if (range[maxAttr]) {
          delete range[maxAttr];
          rangeChanged = true;
        }
      });
      if (rangeChanged) newUiState = Object.assign({}, newUiState, { range });

      // Toggles: use url_param (preferred) or attribute as URL key
      CONFIG.filters.forEach(function (cfg) {
        if (cfg.type !== "toggle") return;
        const urlKey = cfg.url_param || cfg.attribute;
        if (!urlKey) return;
        const st = _state[cfg.id];
        if (!st) return;
        if (st.active) {
          if (!newUiState[urlKey]) {
            newUiState = Object.assign({}, newUiState);
            newUiState[urlKey] = "true";
          }
        } else if (newUiState[urlKey]) {
          newUiState = Object.assign({}, newUiState);
          delete newUiState[urlKey];
        }
      });

      // Native boolean_list: route each attribute through IS refinementList
      CONFIG.filters.forEach(function (cfg) {
        if (cfg.type !== 'boolean_list' || !cfg._parsedBoolLabels) return;
        const st = _state[cfg.id];
        if (!st) return;
        const rl = Object.assign({}, newUiState.refinementList || {});
        let changed = false;
        Object.keys(cfg._parsedBoolLabels).forEach(function (attr) {
          if (!isBoolAttrEnabled(attr)) {
            // Attr not in collectionFacets for this locale: scrub any stale uiState entry
            if (rl[attr]) { delete rl[attr]; changed = true; }
            return;
          }
          if (st.selected.has(attr)) {
            if (!rl[attr] || !rl[attr].includes('true')) { rl[attr] = ['true']; changed = true; }
          } else if (rl[attr]) {
            delete rl[attr]; changed = true;
          }
        });
        if (changed) newUiState = Object.assign({}, newUiState, { refinementList: rl });
      });

      // Non-native filters (conjunctive refinement_list): legacy url_param keys
      CONFIG.filters.forEach(function (cfg) {
        if (isNativeDisjunctive(cfg)) return;
        if (
          cfg.type !== "refinement_list" &&
          cfg.type !== "tag_list" &&
          cfg.type !== "boolean_list"
        )
          return;
        if (!cfg.url_param) return;
        const st = _state[cfg.id];
        if (!st) return;
        if (st.selected.size) {
          const serialized = serializeCustomRouteValues(st.selected);
          if (newUiState[cfg.url_param] !== serialized) {
            newUiState = Object.assign({}, newUiState);
            newUiState[cfg.url_param] = serialized;
          }
        } else if (newUiState[cfg.url_param]) {
          newUiState = Object.assign({}, newUiState);
          delete newUiState[cfg.url_param];
        }
      });

      return newUiState;
    };
    _triggerSearch = function () {
      const helper = window._algoliaHelper;
      const is = window._algoliaIS;
      if (helper && is) {
        // Use IS.setUiState so the change event fires on the real index helper (f),
        // triggering onInternalStateChange -> router.write() -> URL update.
        // window._algoliaHelper is the DerivedHelper (p): helper.search() on p does NOT
        // emit change on f and therefore never updates the URL.
        // Use mainIndex.getIndexId() for the uiState key: this is always the base index
        // name regardless of which sort replica is currently active.
        is.setUiState(function (prevUiState) {
          const indexId = is.mainIndex.getIndexId();
          const indexState = Object.assign({}, prevUiState[indexId]);
          const refinementList = Object.assign({}, indexState.refinementList);

          // Merge selected values from all native-disjunctive filters per attribute
          // so filters sharing the same attribute (e.g. multiple tag_lists on "tags")
          // don't overwrite each other (last-writer-wins).
          // Keep every selected value in uiState.refinementList so InstantSearch routing
          // writes URL/history entries for all custom chips, including price_range values
          // such as "0:50". Values that need quoting are still excluded from
          // addDisjunctiveFacetRefinement in _acfGetDisjunctiveSearchParams and applied
          // through buildFilterString instead, so this refinementList entry is for
          // routing/back-button state rather than Algolia facetFilters.
          var _nativeMerged = {};
          CONFIG.filters.forEach(function (cfg) {
            if (!isNativeDisjunctive(cfg)) return;
            // Attribute absent from collectionFacets: see getEnabledFacets() header. Remove stale
            // uiState entry so it can't linger in the URL and re-seed on the next load.
            if (!isAttrEnabled(cfg.attribute)) {
              delete refinementList[cfg.attribute];
              return;
            }
            var st = _state[cfg.id];
            if (!st) return;
            st._prevSelected = new Set(st.selected);
            if (st.selected.size) {
              if (!_nativeMerged[cfg.attribute]) _nativeMerged[cfg.attribute] = [];
              st.selected.forEach(function (v) {
                _nativeMerged[cfg.attribute].push(v);
              });
            }
          });
          Object.keys(_nativeMerged).forEach(function (attr) {
            refinementList[attr] = _nativeMerged[attr];
          });
          // Clean up attributes where no native-disjunctive filter has selections
          CONFIG.filters.forEach(function (cfg) {
            if (!isNativeDisjunctive(cfg)) return;
            if (!_nativeMerged[cfg.attribute]) delete refinementList[cfg.attribute];
          });

          CONFIG.filters.forEach(function (cfg) {
            if (cfg.type !== 'boolean_list' || !cfg._parsedBoolLabels) return;
            const st = _state[cfg.id];
            if (!st) return;
            st._prevSelected = new Set(st.selected);
            Object.keys(cfg._parsedBoolLabels).forEach(function (attr) {
              if (st.selected.has(attr)) { refinementList[attr] = ['true']; }
              else { delete refinementList[attr]; }
            });
          });

          return Object.assign({}, prevUiState, {
            [indexId]: Object.assign({}, indexState, { refinementList, page: 1 }),
          });
        });
      } else if (helper) {
        // Fallback: IS not yet available, mutate helper directly
        CONFIG.filters.forEach(function (cfg) {
          if (!isNativeDisjunctive(cfg)) return;
          // Attribute absent from collectionFacets crashes the @app render: see getEnabledFacets() header.
          if (!isAttrEnabled(cfg.attribute)) return;
          const st = _state[cfg.id];
          if (!st) return;
          const prevSelected = st._prevSelected || new Set();
          const useQuotedFilterGroup = [...st.selected].some(_needsQuoting);
          prevSelected.forEach(function (val) {
            if (!st.selected.has(val) || useQuotedFilterGroup)
              helper.removeDisjunctiveFacetRefinement(cfg.attribute, val);
          });
          st.selected.forEach(function (val) {
            if (useQuotedFilterGroup) return;
            helper.addDisjunctiveFacetRefinement(cfg.attribute, val);
          });
          st._prevSelected = new Set(st.selected);
        });
        CONFIG.filters.forEach(function (cfg) {
          if (cfg.type !== 'boolean_list' || !cfg._parsedBoolLabels) return;
          const st = _state[cfg.id];
          if (!st) return;
          // Boolean selections are represented by buildFilterString(), not by
          // per-attribute facet refinements that Algolia would combine with AND.
          st._prevSelected = new Set(st.selected);
        });
        helper.setPage(0);
        helper.search();
      } else {
        console.warn("[acf] No helper available to trigger search");
      }
      renderActiveFiltersBar();
      // Always scroll to top of page when a filter is applied or cleared.
      // On desktop, defer one frame so InstantSearch routing/render writes cannot land the viewport at the results.
      const scrollToPageTop = function () {
        window.scrollTo({ top: 0, behavior: "smooth" });
      };
      if (
        window.matchMedia &&
        window.matchMedia("(min-width: 800px)").matches &&
        window.requestAnimationFrame
      ) {
        window.requestAnimationFrame(scrollToPageTop);
      } else {
        scrollToPageTop();
      }
    };

    function ensureInjected() {
      if (!document.querySelector(".acf-sidebar")) {
        _injected = false;
        _enabledFacets = null; // reset so re-injection re-reads collectionFacets/facets
        injectFilters();
      }
    }

    const observer = new MutationObserver(() => {
      if (document.querySelector(".ais-page")) ensureInjected();
    });
    observer.observe(document.body, { childList: true, subtree: true });
    injectFilters();
  }

  document.addEventListener("algolia.hooks.initialize", init);
})();
